import type { ProductCategoryId } from './constants'
import { buildCourseTaxonomy } from './course-taxonomy'
import { TAXONOMY_I18N } from './taxonomy-i18n'

/**
 * 2단 카테고리 체계 (문서 "식품 및 메뉴 카테고리", 2026-09-13).
 * - 축(axis): product = 제조·가공식품(식품공전 유형·공정 계열) / menu = 외식 메뉴(코스·역할 → 조리법·형태)
 * - 대카테고리(major) → 중카테고리(sub). 두 축은 한 목록에 섞지 않는다 (방향이 반대라서).
 * - 파이프라인 연결: 각 항목이 기존 18개 categoryId 에 매핑된다 (프롬프트·게이트·골든셋·맛 칩의 키).
 *   sub 에 categoryId 가 있으면 그것이 major 의 값을 덮는다 (기타·통조림·건조 등 혼합 대분류).
 * - 메뉴 축 sub 의 hint = 문서의 "갈리는 축" 요약. 프롬프트가 조리 골격·인분·원가 축을 잡는 데 쓴다.
 * 대분류 카드 사진은 apps/mobile/src/data/major-images.ts (assets/majors/<axis>-<id>.webp). emoji 는 사진 없는 곳의 폴백용.
 * 2026-10-22 확장: 식품 26대(차·허브, 식물성·비건 추가)·메뉴 8대(국제·서양 메인 추가)와 세부 몇 개.
 *  - 기존 id 는 지우거나 바꾸지 않는다. 1.0.3·1.0.4 앱이 아는 키는 taxonomy-legacy.ts 에 박제했다
 *  - 이름은 뜻을 넓히는 쪽으로만 바꿨다 (옛 결과의 라벨이 틀려지지 않게). 좁아진 예외: sauce.jang, rice-noodle.porridge
 *  - "어떻게·무엇으로·누구에게·코스 어디" 는 이 나무가 아니라 메뉴 꼬리표(menu-tags.ts)로 고른다
 * 2026-10-23 확장 (대표 지시 "카테고리 더 추가"): 식품 27대(떡·한과 추가)·227세부, 메뉴 78세부.
 *  - 근거: 기준 문서 경계 규칙에만 있던 4개(라면 건더기·유성스프, 농축 과채즙, 채소 통조림)와 문서 밖 제품군 후보
 *    (release_kit 53 의 50_추가후보_문서대조, 51_추가후보_제품군). 새 세부는 각 대분류 subs 끝, 새 대분류는 목록 끝
 *  - 반박 검토(52_추가_반박검토)에서 3개 더: 파이프라인 지시문(prompts/categories.ts)의 세부유형에는 있는데 고를 자리가
 *    없던 사탕·캐러멜(snacks.candy), 조미 건어물(seafood.dried-seasoned), 푸딩·컵 디저트(dairy.pudding)
 *  - 식품 세부도 hint 를 갖는다 (sh). 생성 프롬프트에 "분류 설명" 한 줄로 들어간다 (prompts/fixed.ts buildUserPrompt)
 *  - 이름 변경은 뜻을 넓히는 쪽만 (sikhye, tea.cheong, alcohol.traditional·fruit-wine, dried.soup-block,
 *    bakery.cookie, sauce.spice-blend, intl.burger-sandwich). taxonomy-legacy.ts 는 그대로 (새 id 는 옛 앱에 null)
 * 2026-10-24 (대표 위임 결정): 기타(misc) 세부 중 다른 대분류 세부가 뜻을 그대로 담는 중복 5개는 hiddenInPicker 로
 *  새 앱 고르기 화면과 미리보기에서만 뺀다. id·라벨·categoryId 는 남아 저장된 제품, 옛 앱 1.0.3·1.0.4, 서버 검증,
 *  커뮤니티 스냅샷은 그대로 동작한다. 대조표는 release_kit 53 의 53_중복세부_숨김표.md
 * 2026-11-01 (대표 명세 "카테고리 보강" 2장, 셰프·파인 다이닝용): 메뉴 96세부.
 *  - 메인 요리 끝에 초밥·니기리·사시미(회와 분리), 사이드·전채 끝에 타파스·스몰 플레이트
 *  - 국제·서양 메인은 명세의 묶음(파스타, 리조또·그레인, 피자·플랫브레드, 버거·샌드위치, 메인 샐러드)으로 세부를 나눴다.
 *    저장값은 지금처럼 대분류 → 세부 2단이고, 묶음(groups·group)은 고르기 화면의 작은 제목일 뿐이다 (pickerSections)
 *  - 새 세부가 뜻을 다 나눠 가진 옛 세부 3개(리조또·그레인, 피자·플랫브레드, 메인 샐러드)는 hiddenInPicker.
 *    버거·샌드위치·랩은 랩·타코·부리또 자리가 따로 없어 묶음 끝에 두었다가 같은 날 전수 확인 뒤 숨겼다 (아래)
 *  - 기법(수비드·컨핏·발효·큐어링·스모킹·숙성·분자)은 이 나무가 아니라 메뉴 꼬리표 technique (menu-techniques.ts)
 * 2026-11-01 (대표 확정 명세 0장): 셋째 축 course = 코스·테이스팅 (대분류 = 코스 역할, 세부 = 스타일·기법). 표는 course-taxonomy.ts.
 *  메뉴처럼 1인분 접시로 낸다 (menu-tags.ts 의 isMenuLikeAxis). 이 축을 모르는 옛 앱에는 서버가 분류 없는 메뉴로 낮춘다 (taxonomy-legacy.ts)
 * 2026-11-01 (카테고리 전수 확인 뒤, 대표 지시 "주류 종류별과 건조·동결건조 자세하게 세분화"): 식품 267세부.
 *  - 주류 세부 14개(탁주·막걸리 ~ 테킬라·메스칼)를 더하고, 고르기 화면을 묶음 5개(ALCOHOL_GROUPS)로 나눴다.
 *    새 세부가 뜻을 다 나눠 가진 옛 세부 4개(전통주, 과실주, 소주 (증류식 포함), 진·보드카)는 hiddenInPicker
 *  - 건조·동결건조 세부 7개를 더하고, 동결건조 과일·채소는 과일·채소 두 세부로 나눠 hiddenInPicker. 나눈 두 칸은 명세 24번 순서대로
 *    숨긴 옛 칸 바로 뒤(고르기 화면 맨 앞), 나머지 다섯은 기타 건조식품 앞
 *  - 명세보다 좁던 이름 5개를 명세 말로 넓혔다(디핑소스, 양념·마리네이드, 냉동 안주, 수제 트릿, BCAA·전해질). 사탕 세부에 '캔디'를 넣었다
 *  - 식품 대분류 순서를 명세 번호(1 면류 → 26 반려동물 식품)로 바꿨다. 명세 밖 떡·한과·기타는 끝. id 는 그대로라 저장값과 옛 앱은 그대로다
 *  - 영어·일본어·중국어 이름에서 빠졌던 뜻을 채웠다 (스포츠영양·체중조절의 체중조절, 소시지·프랑크의 프랑크 등)
 *  - 메뉴: 파스타 4개 이름을 명세 말 순서로(드라이 파스타 등). 버거·샌드위치·랩은 수제 버거와 같은 칸 둘로 보여 hiddenInPicker
 */
export const AXES = ['product', 'menu', 'course'] as const
export type Axis = (typeof AXES)[number]

/** 저장된 JSON 에서 꺼낸 축 글자 → Axis. 없거나 모르는 값은 product (축 칸이 생기기 전의 행) */
export function axisOf(value: unknown): Axis {
  return (AXES as readonly unknown[]).includes(value) ? (value as Axis) : 'product'
}

/** 만들기 모드: create = 새로 개발, analyze = 이미 있는 레시피의 재료·원가 분석 (레시피 입력 필수) */
export const MODES = ['create', 'analyze'] as const
export type Mode = (typeof MODES)[number]

export interface SubCategory {
  id: string
  ko: string
  en: string
  /** major 의 categoryId 를 덮어쓸 때만 */
  categoryId?: ProductCategoryId
  /**
   * 메뉴 축: 이 중분류가 갈리는 축 (프롬프트 힌트, 한국어).
   * 식품 축: 경계·공정 설명 (2026-10-23 부터, 있는 세부만). buildUserPrompt 의 "분류 설명" 줄
   */
  hint?: string
  /**
   * 새 앱 고르기 화면(subcategory.tsx)과 미리보기에서만 뺀다 (2026-10-24, 기타의 중복 세부).
   * 라벨·검증·저장은 subs 전체를 본다. pickerSubs() 가 거른다
   */
  hiddenInPicker?: boolean
  /** 고르기 화면 묶음 id (major.groups 의 id). 화면의 작은 제목일 뿐 저장·검증·프롬프트에는 쓰지 않는다 */
  group?: string
}

/** 세부 묶음 (2026-11-01, 국제·서양 메인과 주류). 저장값이 아니라 고르기 화면 작은 제목이라 4개 언어를 여기 같이 둔다 */
export interface SubGroup {
  id: string
  ko: string
  en: string
  ja: string
  zh: string
}

export interface MajorCategory {
  id: string
  emoji: string
  ko: string
  en: string
  categoryId: ProductCategoryId
  /** 대분류 설명 (프롬프트 힌트). 메뉴 축은 모두, 식품 축은 떡·한과만 */
  hint?: string
  /** 고르기 화면 묶음 순서. 있으면 세부마다 group 이 있다 (taxonomy.test.ts) */
  groups?: SubGroup[]
  subs: SubCategory[]
}

const s = (id: string, ko: string, en: string, categoryId?: ProductCategoryId): SubCategory =>
  categoryId ? { id, ko, en, categoryId } : { id, ko, en }
/** 식품 세부 + hint (2026-10-23~). 경계(비슷한 세부와 어떻게 가르나)와 공정 축을 한국어로 */
const sh = (
  id: string,
  ko: string,
  en: string,
  hint: string,
  categoryId?: ProductCategoryId,
): SubCategory => (categoryId ? { id, ko, en, categoryId, hint } : { id, ko, en, hint })
/** 고르기 화면에서 숨기는 세부 (다른 대분류 세부가 뜻을 그대로 담는 중복). id·라벨은 남긴다 */
const hidden = (sub: SubCategory): SubCategory => ({ ...sub, hiddenInPicker: true })
const subGroup = (id: string, ko: string, en: string, ja: string, zh: string): SubGroup => ({
  id,
  ko,
  en,
  ja,
  zh,
})
/** 세부 목록에 고르기 화면 묶음 id 를 붙인다 (식품 축 주류). 묶음 안 순서는 목록 순서 그대로 */
const inGroup = (group: string, subs: SubCategory[]): SubCategory[] =>
  subs.map((sub) => ({ ...sub, group }))
/** 메뉴 세부 목록. 칸은 [id 끝 조각, ko, en, hint, 묶음 id(국제·서양 메인만)] */
const m = (
  id: string,
  ko: string,
  en: string,
  hint: string,
  subs: [sid: string, ko: string, en: string, hint: string, group?: string][],
): SubCategory[] =>
  subs.map(([sid, sko, sen, h, group]) => ({
    id: `${id}.${sid}`,
    ko: sko,
    en: sen,
    hint: h,
    ...(group ? { group } : {}),
  }))

// ── 식품(제품) 축: 28 대 ──────────────────────────────────────────────
// 문서 목록에서 중복 항목(같은 이름이 두세 번 나온 것)만 합쳤다.
// 2026-10-22: 24대에 차·허브(tea), 식물성·비건(plant-based)을 더해 26대로 본다 (원본 26대 문서가 없어 가정).
// 새 세부는 각 대분류 subs 끝에 붙였다 (sauce.ganjang, hmr.instant-rice, meat.chicken-breast, pet.dry-food, snacks.chocolate).
// 2026-10-23: 떡·한과(tteok)를 목록 끝에 더해 27대. 새 세부 30개도 각 subs 끝 (기존 세부 순서·id 그대로).
// 2026-11-01 (대표 확정 명세 recipecat-food-category-spec-20261101.md, 26대·148항목): 장류(jang)를 목록 끝에 더해 28대.
//  - 명세 항목마다 고를 수 있는 세부가 하나 이상 있다. 새 세부 19개는 각 subs 끝 (기존 id·순서 그대로)
//  - 명세가 다른 대분류에 둔 제품(즉석밥, 젓갈, 냉동 과채, RTD 차, 콤부차, 식물성 우유, 닭가슴살)은 그 대분류에 세부를
//    하나 더 둔다. 옛 자리도 그대로 보인다(냉동면·육포처럼 두 곳에서 고른다). categoryId 는 옛 세부와 같게 둔다.
//    예외: 장류는 발효 지시문(fermented, 장류 제국·담금·숙성 골격), 절임·저장·발효식품 > 젓갈은 대분류 기본(fermented, 젓갈류 안내)
//  - 이름은 명세 말을 앞에 두되 뜻을 넓히는 쪽으로만 바꿨다 (햄·베이컨 → 햄·가공육 은 베이컨도 가공육이라 넓힘)
//  - 명세가 한 항목으로 묶은 것을 앱이 더 잘게 나눈 곳(RTD 커피·라떼, 스무디·에이드 등)은 그대로 둔다
//  - 대조표: Desktop/claude/recipecat-food-category-audit/00_대조표.md, 시험: apps/api/test/food-taxonomy-spec.test.ts
//  - 속성 꼬리표(비건·저당·보관 온도 등)는 이 나무가 아니라 product-tags.ts
// 2026-11-01 (카테고리 전수 확인 16번): 대분류를 명세 번호 순서(1 면류 → 26 반려동물 식품)로 놓고 명세 밖 떡·한과·기타를 끝에 둔다.
//  위의 "목록 끝에 더했다"는 그때의 자리다. 옛 앱은 자기 표를 들고 있고 서버는 id 로만 찾으므로 순서를 바꿔도 저장값은 그대로다.
//  세부는 지금처럼 옛 세부 앞, 새 세부 끝 (1.0.8 이하 앱이 아는 세부 순서를 지킨다, food-taxonomy-spec.test.ts).
//  묶음이 있는 주류만 예외: 화면이 묶음 순서로 그리므로 세부도 묶음 순서로 놓는다

/**
 * 주류 고르기 화면의 묶음 (2026-11-01 대표 "주류 종류별 자세하게 세분화"). 묶음 제목이 명세 주류 7항목의 말을 담는다.
 * 고르기 화면 세부가 23개라 종류별 작은 제목으로 나눠 보인다. 저장·검증·프롬프트에는 쓰지 않는다
 */
const ALCOHOL_GROUPS: SubGroup[] = [
  subGroup(
    'traditional',
    '전통주·발효주',
    'Traditional & brewed',
    '伝統酒・醸造酒',
    '传统酒·发酵酒',
  ),
  subGroup('wine', '와인·과실주', 'Wine & fruit wine', 'ワイン・果実酒', '葡萄酒·果酒'),
  subGroup('beer', '맥주', 'Beer', 'ビール', '啤酒'),
  subGroup('spirits', '소주·증류주', 'Soju & spirits', '焼酎・蒸留酒', '烧酒·烈酒'),
  subGroup('liqueur-rtd', '리큐르·RTD', 'Liqueurs & RTD', 'リキュール・RTD', '利口酒·即饮酒'),
]

export const PRODUCT_MAJORS: MajorCategory[] = [
  {
    id: 'noodles',
    emoji: '🍜',
    ko: '면류',
    en: 'Noodles',
    categoryId: 'noodles',
    subs: [
      s('fresh', '생면', 'Fresh noodles'),
      s('dried', '건면', 'Dried noodles'),
      sh(
        'cooked',
        '숙면 (삶은면)',
        'Cooked noodles',
        '삶거나 쪄서 바로 쓰게 만든 면. 곤약면 포함. 살균과 냉장 유통, 면 불음 방지가 축.',
      ),
      s('frozen', '냉동면', 'Frozen noodles'),
      s('cup', '즉석 컵면', 'Instant cup noodles'),
      // 2026-11-01 명세 "즉석밥·햇반류 (신규 보강)". HMR·간편식 > 즉석밥 (무균·냉동밥) 과 같은 제품이라 categoryId 도 meal
      sh(
        'instant-rice',
        '즉석밥·햇반류',
        'Ready-cooked rice (instant rice)',
        '밥을 지어 무균 포장하거나 급속냉동해 데워 먹게 만든다. 무균밥(상온)과 냉동밥, 잡곡·현미밥 포함. 취반 수분과 무균 충전·밀봉 조건이 식감과 상온 유통 축, 전자레인지 시간과 1개 중량(g)이 질문. 밥과 소스가 한 용기면 HMR·간편식 > 컵밥, 볶음밥은 냉동식품 > 냉동볶음밥.',
        'meal',
      ),
    ],
  },
  {
    id: 'pickled',
    emoji: '🥬',
    ko: '절임·저장·발효식품',
    en: 'Pickled, preserved & fermented',
    categoryId: 'fermented',
    subs: [
      s('kimchi', '김치', 'Kimchi'),
      s('jangajji', '장아찌', 'Jangajji (soy pickles)'),
      s('pickle', '피클 (초절임)', 'Pickles (vinegar)'),
      s('fermented-pickle', '발효절임', 'Fermented pickles'),
      // 2026-11-01 명세 "젓갈". 수산가공 > 젓갈 (염장발효) 과 같은 제품. 대분류 기본(fermented) 지시문에 젓갈류 관리 안내가 있다
      sh(
        'jeotgal',
        '젓갈',
        'Jeotgal (salted fermented seafood)',
        '새우·멸치·까나리·명란을 소금에 절여 발효·숙성한다. 새우젓·명란젓·창난젓과 멸치액젓·까나리액젓 포함. 염도와 숙성 온도·기간이 맛과 안전 축이고, 저염이면 냉장 유통과 보존 방식이 질문. 장류가 아니라 젓갈류 관리 항목(히스타민·대장균군)으로 본다. 달인 간장에 짧게 숙성하는 게장·연어장은 수산가공 > 게장·장 절임.',
      ),
    ],
  },
  // 2026-11-01 새 대분류 (대표 확정 명세 3번 장류).
  // 소스·양념·조미료 아래 장 세부 4개(aged-gochujang·jang·ganjang·ssamjang)도 그대로 보인다. 같은 장을 두 곳에서 고른다.
  // 장류는 발효 지시문(fermented)으로 간다: 제국·담금·숙성 골격과 장류 염도·총질소 관리가 있다 (소스 쪽 옛 세부는 sauce 그대로)
  {
    id: 'jang',
    // 🫙(병, 2021년 문자)는 옛 안드로이드에서 빈 네모로 보여 옹기 모양 🏺 (사진이 없는 동안 이 글자가 카드에 보인다)
    emoji: '🏺',
    ko: '장류',
    en: 'Jang (Korean fermented pastes)',
    categoryId: 'fermented',
    hint: '콩·쌀·밀을 메주나 누룩(코지)으로 띄워 소금물·고춧가루와 담가 오래 숙성하는 한국 장. 축은 띄우는 방식(메주·코지·바실루스)과 담금 염도, 숙성 기간과 온도. 전통(한식) 장인지 개량(양조·혼합) 장인지가 첫 질문.',
    subs: [
      sh(
        'gochujang',
        '고추장',
        'Gochujang',
        '찹쌀·밀쌀을 엿기름으로 삭혀 메주가루·고춧가루·소금과 버무려 숙성한다. 당화 정도와 고춧가루 비율, 숙성 기간이 단맛·매운맛·색 축. 매운맛 단계와 용기(튜브·통) 단위가 질문. 식초·당을 섞어 바로 쓰는 초고추장 같은 양념은 소스·양념·조미료 > 액상 소스.',
      ),
      sh(
        'doenjang',
        '된장',
        'Doenjang',
        '메주를 소금물에 담가 띄운 뒤 건더기를 걷어 숙성한다(한식 된장). 코지로 담그는 개량 된장 포함. 메주 띄우기와 염도, 숙성 기간이 감칠맛과 쿰쿰함 축. 한식(재래)인지 개량인지가 질문. 고추장·양념을 섞으면 쌈장, 청국장·춘장은 기타 발효장류.',
      ),
      sh(
        'ganjang',
        '간장',
        'Ganjang (Korean soy sauce)',
        '메주를 담근 소금물을 걸러 달이는 한식간장(국간장)과 콩·밀을 코지로 발효해 짜는 양조간장. 담금 염도와 숙성 기간, 달이는 온도가 색·감칠맛 축. 국간장인지 진간장(양조)인지, 산분해간장을 섞는지가 질문.',
      ),
      sh(
        'ssamjang',
        '쌈장',
        'Ssamjang (blended jang)',
        '된장·고추장에 마늘·참기름·견과를 섞은 혼합장. 장 배합비와 숙성도, 가열 살균이 맛과 유통 축. 살균해 상온으로 파는지와 튜브·컵 소포장이 질문.',
      ),
      sh(
        'other',
        '기타 발효장류 (청국장·춘장·막장)',
        'Other jang (cheonggukjang, chunjang, makjang)',
        '청국장(바실루스로 짧게 띄운 콩), 춘장(카라멜색을 낸 짜장용 장), 막장·집장·담북장, 해외 발효장(미소·두반장). 띄우는 미생물과 기간이 장마다 달라 사용자 설명을 따르고, 띄우는 균과 염도·숙성 기간을 정한다. 볶아서 짜장 소스로 팔면 소스·양념·조미료 > 액상 소스.',
      ),
    ],
  },
  {
    id: 'sauce',
    emoji: '🥫',
    ko: '소스·양념·조미료',
    en: 'Sauces & seasonings',
    categoryId: 'sauce',
    subs: [
      sh(
        'liquid',
        '액상 소스',
        'Liquid sauce',
        '병·파우치에 담는 액상 소스 일반. 떡볶이·짜장·마라 소스 포함. 간장·굴소스·바비큐처럼 맞는 세부가 따로 있으면 그쪽.',
      ),
      s('ramen-soup', '라면스프 (분말)', 'Ramen soup powder', 'soup-base'),
      sh(
        'spice-blend',
        '향신료 블렌드·시즈닝 (단일 가루 포함)',
        'Spice blends & seasoning',
        '여러 향신료를 섞은 블렌드와 고춧가루·후추 같은 단일 향신료 가루. 허브솔트 포함. 입도와 살균(증기·건열) 방식, 원산지 표시가 축. 물에 풀어 소스 베이스로 쓰면 분말·과립 소스.',
      ),
      // 2026-11-01: 명세 말 "양념·마리네이드" 로 넓힘 (옛 이름 '(액상 양념)'). 가루 쪽 경계는 hint 로 가른다
      sh(
        'marinade',
        '양념·마리네이드',
        'Seasoning sauces & marinades',
        '고기·해산물·채소를 재우거나 버무리는 양념. 불고기·갈비·닭갈비 양념, 치킨 염지액, 마리네이드 오일 포함. 재우는 시간과 연육(과일 효소), 당·염도가 축. 재료 1kg 당 사용량이 질문. 가루 시즈닝·드라이 럽은 향신료 블렌드·시즈닝, 물에 푸는 가루 베이스는 분말·과립 소스.',
      ),
      sh(
        'seasoning-base',
        '분말·과립 소스 (조미 베이스)',
        'Powder & granule sauce base',
        '물이나 재료에 풀어 쓰는 분말·과립 조미 베이스. 떡볶이·짜장·마라 분말 소스 포함. 라면 한 봉지용 분말스프는 라면스프 (분말).',
      ),
      // 2026-11-01: 명세 말 "디핑소스" 로 넓힘 (옛 이름 '(소포장)'). 소포장은 hint 의 질문으로 남긴다
      sh(
        'dipping',
        '디핑소스',
        'Dipping sauce',
        '찍어 먹는 소스. 치킨·만두·회·튀김에 곁들이는 소포장 컵·파우치와 병·업소용 대용량 포함. 점도와 찍었을 때 묻는 양, 소포장 실링이 축. 1회분 용량(g)과 상온·냉장 유통이 질문. 마요네즈·아이올리·살사·타르타르·쌈장처럼 맞는 세부가 따로 있으면 그쪽.',
      ),
      s('jang', '된장·기타 발효장', 'Doenjang & other jang'),
      s('worcester-steak', '우스터/스테이크 소스', 'Worcestershire & steak sauce'),
      s('mayo', '마요네즈 (유화 소스)', 'Mayonnaise'),
      s('dressing', '드레싱 (비네그레트/크리미)', 'Dressing'),
      s('pesto', '페스토', 'Pesto'),
      s('chutney', '처트니', 'Chutney'),
      s('salsa', '살사', 'Salsa'),
      s('aioli', '아이올리', 'Aioli'),
      s('tartar', '타르타르 소스', 'Tartar sauce'),
      s('ssamjang', '쌈장', 'Ssamjang'),
      s('bbq', '바비큐·구이 소스', 'BBQ & grilling sauce'),
      s('hot-sauce', '핫소스·매운소스 (발효 숙성 포함)', 'Hot & spicy sauce (incl. fermented)'),
      s('oyster', '굴소스', 'Oyster sauce'),
      s('bearnaise', '베어네이즈 유화소스', 'Béarnaise'),
      s('demi-glace', '데미글라스 소스', 'Demi-glace'),
      s('fire-chicken', '불닭 매운소스', 'Extra-hot chicken sauce'),
      s(
        'cream-pasta',
        '파스타·크림 소스 (토마토·오일 포함)',
        'Pasta & cream sauce (incl. tomato, oil)',
      ),
      s('aged-gochujang', '고추장 (장기숙성 포함)', 'Gochujang (incl. aged)'),
      s('truffle-mayo', '트러플 마요네즈 튜브', 'Truffle mayo (tube)'),
      s('ganjang', '간장 (양조·국간장)', 'Soy sauce (ganjang)'),
      // 2026-10-23: 기준 문서 경계 규칙 "라면스프는 소스의 하위" 의 건더기·유성 (분말은 ramen-soup)
      sh(
        'ramen-flakes',
        '라면 건더기스프 (후레이크)',
        'Ramen dried toppings (flakes)',
        '라면·국수 한 봉지에 넣는 건조 채소·고기·계란 조각. 동결건조인지 열풍건조인지, 끓는 물에서 몇 분 안에 살아나는지, 조각 크기와 색이 유지되는지가 축. 분말스프·유성스프와 한 봉지로 나가는지 묻는다. 대용량 건조 채소는 건조·동결건조 > 건조 나물·채소.',
        'soup-base',
      ),
      sh(
        'ramen-oil',
        '라면 유성스프 (조미유)',
        'Ramen seasoning oil',
        '고추·마늘·파 향을 기름에 우려 한 봉지 분량으로 소포장한 라면용 조미유. 향을 뽑는 온도, 산패 관리, 소포장 실링이 축. 한 봉지에 넣는 양이 질문. 병 단위 향미유는 유지류 > 향미유.',
        'soup-base',
      ),
      sh(
        'vinegar',
        '식초 (양조·과일·발효식초)',
        'Vinegar',
        '곡물·과일을 알코올 발효한 뒤 초산 발효한다. 산도와 숙성 기간이 축. 원료(현미·감·사과)와 전통 발효 여부가 원가를 가른다. 물에 타서 바로 마시는 음용 완제품은 음료 > 발효·기능성 음료.',
        'fermented',
      ),
    ],
  },
  {
    id: 'meat',
    emoji: '🥩',
    ko: '축산가공',
    en: 'Processed meat',
    categoryId: 'processed-meat',
    subs: [
      sh(
        'sausage',
        '소시지·프랑크',
        'Sausages & frankfurters',
        '분쇄육을 케이싱에 채운 소시지·프랑크. 순대 포함. 닭가슴살 원료로 고단백을 내세우면 닭가슴살 가공품.',
      ),
      // 2026-11-01 명세가 베이컨을 베이컨·양념육으로 옮겨 이름을 햄·가공육으로 (베이컨도 가공육이라 옛 결과 라벨이 틀리지 않는다)
      sh(
        'ham',
        '햄·가공육',
        'Ham & processed meat',
        '고기를 염지해 성형·가열한 햄과 프레스햄·런천미트 같은 가공육. 염지 침투와 결착, 가열 중심온도가 축. 통·슬라이스 단위가 질문. 삼겹을 염지·훈연해 슬라이스한 베이컨은 베이컨·양념육, 케이싱에 채우면 소시지·프랑크.',
      ),
      s('nugget', '너겟·분쇄성형육', 'Nuggets & formed meat'),
      s('patty', '패티·떡갈비', 'Patty & tteokgalbi'),
      s('jerky', '육포·저키', 'Jerky'),
      // 원료가 닭가슴살이고 고단백을 내세우면 소시지·스테이크 모양이어도 여기.
      // 2026-11-01 명세로 스포츠영양·체중조절에도 같은 제품 자리(sports.high-protein, categoryId 같음)를 둔다
      s(
        'chicken-breast',
        '닭가슴살 가공품 (소시지·큐브·스테이크·바)',
        'Chicken breast products (sausages, cubes, steaks, bars)',
      ),
      sh(
        'marinated',
        '베이컨·양념육 (불고기·양념갈비·닭갈비)',
        'Bacon & marinated meat (bulgogi, galbi)',
        '양념육은 생고기를 양념에 재워 냉장·냉동으로 판다. 고기 대비 양념 비율과 재우는 시간이 원가·수율 축, 연육 방식과 해동 드립이 질문. 예: LA갈비, 소불고기, 돼지 주물럭, 춘천 닭갈비. 베이컨은 삼겹·목살을 염지하고 훈연해 슬라이스한다. 염지 기간과 훈연 온도, 슬라이스 두께가 축. 채소·사리·소스를 한 상자에 담으면 밀키트 > 볶음·구이, 분쇄 성형은 패티·떡갈비, 통햄·프레스햄은 햄·가공육.',
      ),
      sh(
        'jokbal',
        '족발·편육 (삶은 고기)',
        'Jokbal & boiled sliced meat',
        '향신 육수에 삶아 식혀 썰어 판다. 삶는 시간과 육수 재사용이 맛 축, 수율과 냉장 진공·냉동 슬라이스가 질문. 예: 족발, 편육, 수육, 오향장육.',
      ),
      // 계란은 식육이 아니라 육가공 지시문(아질산·식육 함량)을 타지 않게 other 로 둔다
      sh(
        'egg',
        '알가공품 (구운란·훈제란·계란장)',
        'Egg products',
        '계란을 굽거나 삶아 염지하거나 장에 절인다. 식품유형은 알가공품. 염도와 가열 조건이 껍질 벗김·식감 축, 살모넬라 관리와 냉장 유통이 질문. 업소용 액란·지단도 여기.',
        'other',
      ),
    ],
  },
  {
    id: 'seafood',
    emoji: '🐟',
    ko: '수산가공',
    en: 'Processed seafood',
    categoryId: 'processed-seafood',
    subs: [
      s('fish-cake', '어묵·맛살', 'Fish cake & crab stick'),
      sh(
        'jeotgal',
        '젓갈 (염장발효)',
        'Salted fermented seafood',
        '수산물을 소금에 오래 절여 발효한다. 멸치액젓·까나리액젓 같은 액젓 포함. 염도와 숙성 기간이 축. 달인 간장에 짧게 숙성하는 게장·연어장은 게장·장 절임.',
      ),
      s('smoked-salmon', '훈제연어', 'Smoked salmon'),
      s('semi-dried', '반건조 생선', 'Semi-dried fish'),
      s('seaweed', '조미김', 'Seasoned seaweed'),
      sh(
        'soy-marinated',
        '게장·장 절임 (간장게장·연어장·새우장)',
        'Soy-marinated seafood (gejang)',
        '생물 수산물을 달인 간장이나 양념에 짧게 숙성해 냉장·냉동으로 판다. 장의 염도·당도와 숙성 일수, 생물 위생(냉동 처리·저온 유지)이 축. 양념게장 포함. 오래 염장 발효한 젓갈·액젓은 젓갈.',
      ),
      // 10/23 반박 추가: 파이프라인 수산가공 지시문에 건포류가 있는데 고를 자리가 없었다 (반건조 생선·조미김뿐)
      sh(
        'dried-seasoned',
        '조미 건어물 (진미채·쥐포·어포)',
        'Seasoned dried seafood',
        '오징어·쥐치·명태를 말려 조미액에 재우고 굽거나 찢는다. 조미액 당·염도와 건조 뒤 수분, 굽기 정도가 식감과 상온 유통 축. 탈산소제·지퍼 포장과 소분 단위가 질문. 예: 진미채, 쥐포, 황태채, 버터구이 오징어. 반쯤 말린 생선은 반건조 생선, 볶아서 반찬으로 팔면 HMR·간편식 > 완제 반찬, 고기 육포는 축산가공 > 육포.',
      ),
      // 2026-11-01 명세 "기타 수산가공품"
      sh(
        'other',
        '기타 수산가공품',
        'Other seafood products',
        '어묵·젓갈·훈제·반건조·조미김·게장·조미 건어물에 맞지 않는 수산가공품. 예: 냉동 손질 새우·오징어, 연어 큐브·스테이크, 해산물 육수·엑기스, 다시마·미역 가공품. 원물 선도와 손질 수율, 냉동 글레이징과 가열 조건이 축. 생물·냉동·가열 중 어느 상태로 파는지가 질문. 참치캔은 통조림·병조림 > 참치캔.',
      ),
    ],
  },
  {
    id: 'frozen',
    emoji: '🧊',
    ko: '냉동식품',
    en: 'Frozen',
    categoryId: 'meal',
    subs: [
      s('dumpling', '만두·교자', 'Dumplings'),
      s('fried-rice', '냉동볶음밥', 'Frozen fried rice'),
      // 2026-11-01: 명세 말 "냉동 안주" 로 넓힘 (옛 이름 '(감바스·닭꼬치)'). 예는 hint 로
      sh(
        'bar-snack',
        '냉동 안주',
        'Frozen bar snacks',
        '술안주로 데우거나 튀겨 내는 냉동 제품. 감바스, 닭꼬치, 닭껍질 튀김, 오돌뼈, 순살 닭발 포함. 에어프라이어·전자레인지·팬 조리 시간과 해동 뒤 식감, 양념 별첨이 축. 한 봉지가 몇 인분인지가 질문. 만두는 만두·교자, 돈까스는 냉동 커틀릿·돈까스, 핫도그·호떡 같은 간식은 냉동 피자·간식.',
      ),
      s('cutlet', '냉동 커틀릿·돈까스', 'Frozen cutlet'),
      s('noodle', '냉동면', 'Frozen noodles', 'noodles'),
      sh(
        'ice-pop',
        '빙과·아이스바·셔벗',
        'Ice pops & sorbet',
        '과즙·당·안정제를 얼린 얼음과자. 당도와 안정제가 얼음 결정 크기와 식감 축. 막대·컵·튜브 형태와 저장 온도가 질문. 우유·유크림이 주체면 유제품 > 아이스크림·젤라또.',
        'dessert',
      ),
      sh(
        'snack',
        '냉동 피자·간식 (핫도그·호떡·붕어빵)',
        'Frozen pizza & snacks',
        '데우거나 굽기만 하면 되는 냉동 간식. 에어프라이어·전자레인지 조리 기준과 반죽 수분이 식감 축. 낱개 포장과 조리법 표시가 질문. 술안주용 감바스·닭꼬치는 냉동 안주, 만두는 만두·교자.',
      ),
      // 2026-11-01 명세 "냉동 과채 (IQF)". 과채가공 > 냉동 과채 (IQF) 와 같은 제품이라 categoryId 도 같다
      sh(
        'iqf',
        '냉동 과채 (IQF)',
        'IQF frozen produce',
        '과일·채소를 손질해(채소는 데쳐) 낱알로 급속동결한다. 데치는 조건과 동결 속도, 얼음막(글레이징)과 덩어리 지지 않게 하는 처리가 축. 소포장 지퍼백인지 업소용 대포장인지가 질문. 갈아서 얼린 퓨레는 과채가공 > 과채 퓨레.',
        'fruit-vegetable',
      ),
      // 2026-11-01 명세 "기타 냉동 가공식품"
      sh(
        'other',
        '기타 냉동 가공식품',
        'Other frozen foods',
        '만두·볶음밥·커틀릿·안주·간식·면·과채 세부에 맞지 않는 냉동 가공식품. 예: 냉동 전·부침개, 냉동 수프·소스 큐브, 냉동 떡, 냉동 곱창·막창 구이. 급속동결 속도와 해동·재가열 뒤 식감, 냉동 건조(냉동화상)를 막는 포장이 축. 전자레인지·에어프라이어·팬 중 무엇으로 데우는지가 질문.',
      ),
    ],
  },
  {
    id: 'hmr',
    emoji: '🍱',
    ko: 'HMR·간편식',
    en: 'Ready meals',
    categoryId: 'meal',
    subs: [
      sh(
        'retort-soup',
        '레토르트 국·탕·찌개',
        'Retort soup & stew',
        '데우기만 하면 되는 레토르트 국·탕·찌개. 사골육수·냉면육수 파우치 포함. 레토르트 살균 조건과 건더기 식감이 축.',
      ),
      s('cup-rice', '컵밥', 'Cup rice'),
      s('porridge', '죽·포리지', 'Porridge'),
      s('bowl-sauce', '덮밥소스', 'Rice bowl sauce', 'sauce'),
      s('instant-pasta', '즉석 파스타', 'Instant pasta'),
      // 밥만 들어 있으면 여기, 밥과 소스가 한 용기면 cup-rice, 볶음밥은 frozen.fried-rice
      s('instant-rice', '즉석밥 (무균·냉동밥)', 'Instant cooked rice'),
      sh(
        'banchan',
        '완제 반찬 (볶음·조림·무침)',
        'Ready-made banchan',
        '그대로 또는 데워 먹는 반찬 완제품. 가열·살균 방식이 냉장 소비기한을 가르고, 소분 용기 단위와 몇 종 세트인지가 질문. 예: 멸치볶음, 장조림, 진미채볶음, 콩자반. 손질 재료를 직접 조리하는 키트는 밀키트 > 간편 반찬 세트, 김치·장아찌는 절임·저장·발효식품.',
      ),
      sh(
        'dosirak',
        '도시락·김밥·샌드위치 (즉석섭취)',
        'Lunch boxes, gimbap & sandwiches (ready to eat)',
        '밥·빵에 반찬·속재료를 담아 바로 먹는 냉장 제품(편의점·케이터링). 냉장 유통과 짧은 소비기한, 밥 노화와 속재료 수분 이동이 축. 칸 수와 1식 원가 상한이 질문. 냉동 김밥도 여기. 상온 컵밥은 컵밥, 비건을 내세우면 식물성·비건 대안 > 비건 간편식.',
      ),
    ],
  },
  {
    id: 'meal-kit',
    emoji: '🍲',
    ko: '밀키트',
    en: 'Meal kits',
    categoryId: 'meal',
    subs: [
      s('soup-stew', '국·탕·찌개 밀키트', 'Soup & stew kit'),
      s('stir-fry', '볶음·구이 밀키트', 'Stir-fry & grill kit'),
      s('noodle', '면 밀키트', 'Noodle kit'),
      // 10/23 반박: hmr.banchan(완제 반찬)과 이름이 가까워 경계를 이쪽에도 적는다
      sh(
        'side-dish',
        '간편 반찬 세트',
        'Side-dish set',
        '손질한 재료와 양념을 나눠 담아 집에서 볶거나 무쳐 완성하는 반찬 키트. 손질 상태와 양념 별첨, 집에서 걸리는 조리 시간이 축. 몇 종 세트인지가 질문. 데우거나 그대로 먹는 완제품은 HMR·간편식 > 완제 반찬.',
      ),
      s('pasta', '파스타 밀키트', 'Pasta kit'),
      s('hot-pot', '전골·샤브 밀키트', 'Hot pot & shabu kit'),
      s('salad', '샐러드 밀키트 (세척채소+드레싱)', 'Salad kit (washed greens + dressing)'),
      s('budae', '부대찌개 밀키트', 'Army stew kit'),
      s('gambas', '감바스 밀키트', 'Gambas kit'),
      s('mille-feuille', '밀푀유나베 밀키트', 'Mille-feuille nabe kit'),
      s('malatang', '마라탕 밀키트', 'Malatang kit'),
      s('seafood-steam', '해물찜 밀키트', 'Seafood steam kit'),
      s('seafood-pancake', '해물파전 밀키트', 'Seafood pancake kit'),
      s('chilled-pasta', '냉장 레스토랑 파스타 밀키트', 'Chilled restaurant pasta kit'),
      sh(
        'tteokbokki',
        '떡볶이·분식 밀키트',
        'Tteokbokki & Korean snack kit',
        '떡·어묵·소스(분말·액상)를 한 봉에 담아 끓이기만 하면 되는 키트. 소스 매운맛 단계와 떡 굳음(상온·냉동)이 축. 국물떡볶이, 라볶이, 로제떡볶이, 컵떡볶이 포함. 떡만 팔면 떡·한과 > 요리용 떡, 소스만 팔면 소스.',
      ),
      // 2026-11-01 명세 "기타 밀키트"
      sh(
        'other',
        '기타 밀키트',
        'Other meal kits',
        '국·탕·찌개, 볶음·구이, 면, 파스타, 전골·샤브, 샐러드와 이름 붙은 밀키트(부대찌개·감바스·마라탕 등)에 맞지 않는 키트. 예: 덮밥 키트, 브런치 플레이트 키트, 캠핑 바비큐 세트, 디저트 만들기 키트. 손질 상태와 소스 별첨, 집에서 걸리는 조리 시간, 냉장·냉동 유통이 축. 한 상자가 몇 인분인지가 질문.',
      ),
    ],
  },
  {
    id: 'bakery',
    emoji: '🥐',
    ko: '제과·제빵',
    en: 'Bakery & pastry',
    categoryId: 'bakery',
    subs: [
      s('premix', '베이커리 프리믹스', 'Baking premix'),
      s('frozen-dough', '냉동 생지', 'Frozen dough'),
      sh(
        'cookie',
        '쿠키·비스킷·구움과자 (마들렌·휘낭시에)',
        'Cookies, biscuits & baked sweets',
        '쿠키·비스킷·크래커와 마들렌·휘낭시에·파운드·마카롱 같은 구움과자. 수분 활성도와 포장 방식이 상온 유통기한 축.',
      ),
      s('cake', '케이크류', 'Cakes'),
      sh(
        'bread',
        '빵류 (식빵·소금빵·크루아상·페이스트리)',
        'Bread & pastry (baked)',
        '구워서 파는 완제 빵. 발효 리드타임과 굽기 설비가 축. 당일 판매인지 냉동 완제(해동 판매)인지, 노화·곰팡이 관리가 질문. 도넛·튀김빵도 여기. 굽기 전 반죽은 냉동 생지, 속을 넣어 바로 먹는 샌드위치는 HMR > 도시락·김밥·샌드위치, 비건을 내세우면 식물성·비건 대안 > 비건 베이커리·디저트.',
      ),
    ],
  },
  {
    id: 'snacks',
    emoji: '🍿',
    ko: '스낵·과자',
    en: 'Snacks',
    categoryId: 'snacks',
    subs: [
      sh(
        'chips',
        '스낵·칩',
        'Chips',
        '튀기거나 구운 칩·스낵. 부각·누룽지칩·쌀과자 포함. 유탕 여부와 산패 관리가 축.',
      ),
      s('bar', '시리얼바·견과바', 'Cereal & nut bar'),
      sh(
        'gummy',
        '젤리·구미',
        'Gummies',
        '젤라틴·펙틴·곤약으로 굳힌 젤리. 곤약젤리 포함. 기능성 원료를 내세우면 건강기능식품 > 구미, 한천으로 굳힌 양갱은 떡·한과 > 양갱·앙금.',
      ),
      s('popcorn', '팝콘', 'Popcorn'),
      s('chocolate', '초콜릿', 'Chocolate'),
      // 10/23 반박 추가: 식품공전 캔디류(사탕·캐러멜)가 고를 자리가 없었다 (젤리·구미와 초콜릿뿐)
      // 2026-11-01: 명세 "초콜릿·캔디" 의 '캔디' 로 찾을 수 있게 이름 앞에 둔다 (옛 이름 '사탕·캐러멜·엿 (누가·마시멜로)')
      sh(
        'candy',
        '캔디·사탕·캐러멜 (엿·누가·마시멜로)',
        'Candy & caramels',
        '설탕·물엿을 졸여 굳힌 사탕류. 하드캔디, 캐러멜, 누가, 마시멜로, 호박엿·쌀엿 포함. 졸이는 온도와 남는 수분이 단단함과 흡습 축. 낱개 포장과 끈적임 관리가 질문. 젤라틴·펙틴으로 말랑하게 굳히면 젤리·구미, 흐르는 조청은 기타 > 꿀·조청, 깨·견과를 엿으로 굳힌 강정은 떡·한과 > 한과.',
      ),
      // 2026-11-01 명세 "프로틴 스낵"
      sh(
        'protein-snack',
        '프로틴 스낵',
        'Protein snacks',
        '유청·대두·완두 단백을 넣어 단백질을 높인 칩·퍼프·쿠키·크리스프. 단백 원료가 많을수록 딱딱하고 텁텁해지는 식감과 압출·굽기 조건이 축. 1봉 단백질 g 목표가 질문. 단백질 표시는 함량 기준을 확인한 뒤에만 쓴다. 막대 모양 바는 스포츠영양·체중조절 > 프로틴바.',
      ),
    ],
  },
  {
    id: 'dairy',
    emoji: '🥛',
    ko: '유제품·발효유',
    en: 'Dairy & fermented milk',
    categoryId: 'dairy',
    subs: [
      s('yogurt', '발효유·요거트', 'Fermented milk & yogurt'),
      s('natural-cheese', '자연치즈', 'Natural cheese'),
      s('processed-cheese', '가공치즈', 'Processed cheese'),
      s('ice-cream', '아이스크림·젤라또', 'Ice cream & gelato'),
      sh(
        'butter-cream',
        '버터·크림 (발효버터·생크림)',
        'Butter & cream',
        '유크림을 교반해 버터를 만들거나 크림을 살균해 판다. 유지방률과 배양(발효) 여부가 풍미 축. 버터에 꿀·앙금을 섞어 바르는 제품은 기타 > 스프레드, 식물성 유지는 유지류.',
      ),
      // 10/23 반박 추가: 파이프라인 dessert 지시문에 푸딩 분기가 있는데 식품 축에 고를 자리가 없었다 (메뉴 축 컵 디저트만)
      sh(
        'pudding',
        '푸딩·컵 디저트 (커스터드·판나코타)',
        'Pudding & cup desserts',
        '우유·크림·달걀을 데워 젤라틴·전분으로 굳히거나 쪄서 굳힌 컵 디저트. 겔화제 선택과 살균·충전 온도가 식감과 냉장 소비기한 축. 컵 크기와 소스·토핑 층 구성이 질문. 예: 커스터드 푸딩, 우유 푸딩, 판나코타, 크림 브륄레. 과즙 젤리 컵은 스낵·과자 > 젤리·구미, 떠먹는 요거트는 발효유·요거트.',
        'dessert',
      ),
    ],
  },
  {
    // 두부·템페 완제품과 대체육. 콩가루·대두단백 원료는 grains.soy. 2026-11-01 명세로 식물성 우유(beverage.plant-milk 와 같은 제품)도 여기서 고른다
    id: 'plant-based',
    emoji: '🌱',
    ko: '식물성·비건 대안',
    en: 'Plant-based & vegan alternatives',
    categoryId: 'other',
    subs: [
      s(
        'plant-meat',
        '식물성 고기 (패티·너겟·소시지·민스)',
        'Plant-based meat (patties, nuggets, sausages, mince)',
        'grain-legume-nut',
      ),
      s('tofu-tempeh', '두부·템페', 'Tofu & tempeh', 'grain-legume-nut'),
      s('dairy-alt', '식물성 요거트·치즈', 'Plant-based yogurt & cheese'),
      s('vegan-meal', '비건 간편식·도시락', 'Vegan ready meals', 'meal'),
      s('vegan-bakery', '비건 베이커리·디저트', 'Vegan bakery & desserts', 'bakery'),
      // 2026-11-01 명세 "식물성 우유·식물성 단백질 제품"
      sh(
        'plant-milk',
        '식물성 우유',
        'Plant-based milk',
        '오트·아몬드·콩·쌀·코코넛을 갈거나 효소로 당화해 우유처럼 마시게 만든다. 원료 비율과 효소 처리, 균질로 막는 침전·분리, 칼슘 강화 여부가 축. 살균 방식(멸균 무균충전·냉장)과 바리스타용 거품 품질이 질문.',
        'beverage',
      ),
      sh(
        'plant-protein',
        '식물성 단백질 제품',
        'Plant protein products',
        '대두·완두·밀·병아리콩 단백을 조직화하거나 분리해 요리 재료로 판다. 조직화 대두단백(TVP), 완두 단백 크럼블, 세이탄 포함. 압출 조직화 조건과 수화 뒤 식감, 콩 비린내 가림이 축. 원료 단백으로 팔지 조리용 반제품으로 팔지가 질문. 고기 모양으로 성형하면 식물성 고기, 두부는 두부·템페, 타 마시는 단백 분말은 스포츠영양·체중조절 > 단백질 파우더.',
        'grain-legume-nut',
      ),
    ],
  },
  {
    id: 'supplement',
    emoji: '💊',
    ko: '건강기능식품',
    en: 'Supplements',
    categoryId: 'other',
    subs: [
      s('powder-stick', '분말스틱', 'Powder stick'),
      s('jelly-stick', '젤리스틱', 'Jelly stick'),
      s('tablet', '정제·캡슐', 'Tablet & capsule'),
      s('ampoule', '액상앰플', 'Liquid ampoule'),
      s('gummy', '구미 (젤리)', 'Gummy'),
      // 원료 우선 (닭가슴살 가공과 같은 규칙): 홍삼·인삼이 주원료면 스틱·정·앰플 모양이어도 여기
      sh(
        'red-ginseng',
        '홍삼·인삼 (농축액·스틱·절편)',
        'Red ginseng & ginseng',
        '홍삼·인삼이 주원료면 스틱·정·앰플 모양이어도 여기. 원료 연근과 농축 배수가 원가 축. 건강기능식품으로 신고할지 일반식품(인삼·홍삼제품)으로 팔지 먼저 정한다. 일반식품이면 기능성 문구를 쓰지 않는다.',
      ),
    ],
  },
  {
    id: 'sports',
    emoji: '💪',
    ko: '스포츠영양·체중조절',
    en: 'Sports nutrition & weight management',
    categoryId: 'other',
    subs: [
      s('protein-powder', '단백질 파우더', 'Protein powder'),
      s('protein-bar', '프로틴바', 'Protein bar', 'snacks'),
      s('shake', '대체식 쉐이크 (RTD)', 'Meal-replacement shake', 'beverage'),
      // 2026-11-01: 명세 말 "BCAA·전해질" 로 넓힘 (옛 이름 '분말'). 스틱·정제·병 제품도 여기
      sh(
        'electrolyte-powder',
        'BCAA·전해질',
        'BCAA & electrolytes',
        '운동 중·후에 마시는 BCAA(분지쇄 아미노산)와 전해질(나트륨·칼륨·마그네슘) 보충 제품. 물에 타는 분말·스틱, 발포정, 바로 마시는 병 제품 포함. 아미노산 비율과 전해질 함량, 쓴맛 가림과 용해성이 축. 1회 섭취량과 포장 단위가 질문. 일반 이온음료는 음료 > 이온·전해질 음료.',
      ),
      // 2026-11-01 명세 "닭가슴살·고단백 가공 연계". 축산가공 > 닭가슴살 가공품과 같은 제품이라 categoryId 도 processed-meat
      sh(
        'high-protein',
        '닭가슴살·고단백 가공',
        'Chicken breast & high-protein products',
        '닭가슴살·소 홍두깨처럼 지방이 적은 고기로 단백질을 높인 운동·식단용 가공품. 소시지·큐브·스테이크·바 모양과 염지·수비드·훈연 가열이 촉촉함과 짠맛 축. 1팩 단백질 g 목표와 냉장·냉동 유통이 질문. 고단백 표시는 함량 기준을 확인한 뒤에만 쓴다. 공정 기준은 축산가공 > 닭가슴살 가공품.',
        'processed-meat',
      ),
    ],
  },
  {
    id: 'beverage',
    emoji: '🧃',
    ko: '음료',
    en: 'Beverages',
    categoryId: 'beverage',
    subs: [
      s('soda', '청량·탄산음료', 'Carbonated soft drink'),
      s('juice', '과채주스 (NFC·착즙)', 'Fruit & vegetable juice', 'juice'),
      s('rtd-tea', '티 · 액상차 (RTD)', 'RTD tea'),
      s('functional', '발효·기능성 음료', 'Fermented & functional drink'),
      s('soda-base', '탄산음료 베이스', 'Soda base (syrup)'),
      s('protein', '단백질 음료 (RTD)', 'Protein drink (RTD)'),
      s('kombucha', '콤부차 (발효 차 음료)', 'Kombucha'),
      s('smoothie', '스무디 (과일·채소 퓨레)', 'Smoothie', 'juice'),
      s('ade', '에이드 (과즙·탄산)', 'Ade (sparkling fruit drink)'),
      s('electrolyte', '이온·전해질 음료', 'Electrolyte drink'),
      s('plant-milk', '식물성 음료 (오트/아몬드 밀크)', 'Plant milk (oat/almond)'),
      s('probiotic', '유산균 발효음료', 'Probiotic drink'),
      sh(
        'sikhye',
        '식혜·수정과 (전통 음료)',
        'Sikhye & sujeonggwa',
        '식혜는 엿기름으로 밥을 삭혀 단맛을 낸다. 수정과는 계피·생강을 달여 곶감을 띄운다. 당화 시간과 살균 뒤 밥알 식감이 축.',
      ),
      s('cold-brew-latte', '콜드브루 라떼 RTD', 'Cold brew latte (RTD)'),
      s('energy', '기능성·에너지 음료', 'Functional & energy drink'),
      // 2026-11-01 명세 이름 "콜라겐·이너뷰티 음료". 미용 효과처럼 읽히지 않게 hint 로 효능 문구를 막는다
      sh(
        'collagen',
        '콜라겐·이너뷰티 음료',
        'Collagen & inner-beauty drink',
        '저분자 콜라겐·히알루론산 같은 원료를 넣어 마시는 음료·앰플형 음료. 원료 함량과 비린 맛 가림, 살균 뒤 침전과 색 변화가 축. 1병 용량과 원료 함량(mg)이 질문. 피부·미용 효과 문구는 쓰지 않는다. 기능성 원료로 신고해 팔면 건강기능식품 > 액상앰플.',
      ),
      s('condition', '컨디션 케어 음료', 'Hangover-care drink'),
      s('zero-soda', '제로·저당 탄산음료', 'Zero & low-sugar soda'),
      s('vitamin-water', '비타민워터', 'Vitamin water'),
      s('hpp-shot', 'HPP 콜드프레스 주스샷', 'HPP cold-pressed shot', 'juice'),
      s('aseptic-plant-protein', '무균 식물성 단백질 음료', 'Aseptic plant protein drink'),
      // 2026-10-23: 기준 문서 경계 규칙 "주스는 음료의 하위" 의 농축과·채즙 (juice 는 NFC/착즙이라 농축액을 못 받는다)
      sh(
        'juice-concentrate',
        '농축 과채즙 (농축액)',
        'Fruit & vegetable juice concentrate',
        '과일·채소 즙을 졸여 만든 원료 또는 업소용 제품. 당도(Brix)와 희석 배수, 냉동·무균 포장이 축. 바로 마시는 주스는 과채주스, 과육째 간 것은 과채가공 > 과채 퓨레, 탄산 시럽은 탄산음료 베이스.',
        'juice',
      ),
      sh(
        'health-juice',
        '건강즙·진액 (파우치)',
        'Health juice & extract pouches',
        '채소·과일·약초를 달이거나 착즙해 1회분 파우치에 담는다. 달임 시간·농축 정도와 열충전 살균이 축. 포 수(30포·60포) 박스 단위가 질문. 예: 양배추즙, 배도라지즙, 흑마늘즙. 효능 문구는 쓰지 않는다. 홍삼·인삼이 주원료면 건강기능식품 > 홍삼·인삼, 당에 재운 청은 차·허브 음료 > 과일청·전통차.',
        'juice',
      ),
    ],
  },
  {
    id: 'coffee',
    emoji: '☕',
    ko: '커피',
    en: 'Coffee',
    categoryId: 'beverage',
    subs: [
      s('rtd', 'RTD 커피 (병·캔)', 'RTD coffee'),
      s('cold-brew-concentrate', '콜드브루 원액 · 농축', 'Cold brew concentrate'),
      s('drip-bag', '드립백', 'Drip bag'),
      s('ground', '원두 (홀빈·분쇄)', 'Coffee beans (whole & ground)'),
      s('mix-stick', '커피 믹스 (스틱 분말)', 'Coffee mix stick'),
      s('capsule', '캡슐 커피 (호환/전용)', 'Coffee capsule'),
      s('rtd-latte', 'RTD 라떼', 'RTD latte'),
      s('instant', '인스턴트 커피', 'Instant coffee'),
      s('bulk-concentrate', '액상 커피원액 벌크', 'Bulk coffee concentrate'),
      s('cold-brew-bag', '콜드브루 백 (침출백)', 'Cold brew bag'),
      s('nitro', '콜드브루 캔 (니트로 포함)', 'Cold brew can (incl. nitro)'),
      s('specialty-drip', '스페셜티 드립백 (질소충전)', 'Specialty drip bag (nitrogen-flushed)'),
    ],
  },
  {
    // 우리거나 타 마시는 차. 2026-11-01 명세로 RTD 차·콤부차도 여기서 고른다 (beverage.rtd-tea·kombucha 와 같은 제품)
    id: 'tea',
    emoji: '🍵',
    ko: '차·허브 음료',
    en: 'Tea & herbal drinks',
    categoryId: 'beverage',
    subs: [
      s('leaf', '잎차·티백', 'Loose-leaf & tea bags'),
      s('herbal', '허브티·블렌드', 'Herbal & blended tea'),
      s('powder', '분말차·말차', 'Powdered tea & matcha'),
      sh(
        'cheong',
        '과일청·전통차 (유자·레몬·생강·대추)',
        'Fruit tea preserves & Korean tea',
        '과일·생강·대추를 당에 재우거나 달여 물에 타 마시는 차. 카페용 수제청(레몬청·자몽청) 포함. 당 비율과 숙성 기간, 살균 여부가 축. 빵에 바르는 잼은 과채가공 > 잼, 설탕에 졸여 건더기를 먹는 당절임은 과채가공 > 당절임·콤포트.',
      ),
      s('concentrate', '차 원액·티 시럽', 'Tea concentrate & syrup'),
      // 2026-11-01 명세 "RTD 차·기능성 차·콤부차"
      sh(
        'rtd',
        'RTD 차',
        'RTD tea (bottled & canned)',
        '우린 차를 병·캔·종이팩에 담아 바로 마시게 판다. 녹차·보리차·옥수수수염차·밀크티 포함. 추출 온도·시간과 살균(열충전·레토르트·무균) 뒤 색·향 유지, 침전 방지가 축. 무가당인지 가당인지와 용량(ml)이 질문. 우려 마시는 차는 잎차·티백, 발효한 차 음료는 콤부차.',
      ),
      sh(
        'functional',
        '기능성 차',
        'Functional tea',
        '현미녹차·우엉차·작두콩차·옥수수수염차처럼 곡물·뿌리·잎을 볶거나 덖어 우리는 차와 원료를 내세운 블렌딩 차. 볶음 정도와 입자 크기, 티백 침출 속도가 축. 원료 원산지와 함량 표시가 질문. 효능·기능 문구는 쓰지 않는다. 기능성 원료로 신고해 팔면 건강기능식품, 꽃·허브를 섞은 차는 허브티·블렌드.',
      ),
      sh(
        'kombucha',
        '콤부차',
        'Kombucha (fermented tea)',
        '단 차에 스코비(초산균·효모)를 넣어 발효하고 걸러 병입한다. 발효 기간과 당·산도, 알코올이 주류 기준(한국 1도, 미국 0.5%) 아래로 머무는지가 축. 2차 발효 탄산과 살균 여부(생균·살균)가 질문. 물에 타 마시는 분말 콤부차도 여기.',
      ),
    ],
  },
  {
    id: 'alcohol',
    emoji: '🍶',
    ko: '주류',
    en: 'Alcohol',
    categoryId: 'alcohol',
    // 2026-11-01 종류별 세분화: 고르기 화면은 묶음(ALCOHOL_GROUPS) 순서, 묶음 안은 아래 순서 그대로.
    // 묶음으로 화면 자리가 새로 정해져서 옛 세부도 묶음 안의 제자리로 옮겼다 (id·이름은 그대로)
    groups: ALCOHOL_GROUPS,
    subs: [
      ...inGroup('traditional', [
        // 2026-11-01: 탁주·막걸리, 약주·청주, 사케가 뜻을 나눠 가져 고르기 화면에서 숨김
        hidden(
          sh(
            'traditional',
            '전통주 (탁주·약주·막걸리·청주)',
            'Traditional liquor (takju, yakju, makgeolli, cheongju)',
            '쌀·누룩으로 빚는 발효주. 탁주·약주·청주(사케 포함). 누룩 종류와 발효 온도, 거르는 방식이 축. 살균하지 않은 막걸리는 생막걸리, 증류하면 소주.',
          ),
        ),
        sh(
          'takju',
          '탁주·막걸리',
          'Takju & makgeolli',
          '쌀·밀을 누룩으로 발효해 맑게 거르지 않고 탁하게 빚는다. 누룩 종류와 발효 온도·기간, 단맛을 남기는 정도(감미료 여부)가 축. 살균 여부와 유통 온도, 병 용량이 질문. 살균하지 않은 냉장 유통은 생막걸리, 맑게 거르면 약주·청주, 병 속 탄산을 살리면 스파클링 발효주.',
        ),
        s('fresh-makgeolli', '생막걸리 (살균 안 함)', 'Fresh makgeolli (unpasteurized)'),
        sh(
          'yakju',
          '약주·청주',
          'Yakju & cheongju',
          '쌀·누룩으로 빚은 술덧을 맑게 걸러 숙성한다. 거르는 방식(용수·여과)과 숙성 기간이 맑기와 향 축, 단맛과 도수 조절이 질문. 일본식 정미·코지 공정이면 사케, 유자를 넣은 저도 약주는 유자 저도 약주.',
        ),
        s('yuzu-yakju', '유자 저도 약주', 'Yuzu low-ABV yakju'),
        sh(
          'sake',
          '사케',
          'Sake',
          '쌀을 깎아(정미) 코지와 효모로 발효하는 일본식 청주. 정미율과 효모, 저온 발효 기간이 향 축. 준마이·긴조 같은 등급(정미율·양조 알코올 첨가 여부)과 살균 여부(나마자케)가 질문. 국내 누룩으로 빚은 맑은 술은 약주·청주.',
        ),
        s(
          'sparkling-traditional',
          '스파클링 발효주 (발효 탄산)',
          'Sparkling fermented wine (naturally carbonated)',
        ),
        s('omija-sparkling', '오미자 스파클링 발효주', 'Omija sparkling wine'),
      ]),
      ...inGroup('wine', [
        // 2026-11-01: 와인, 사과주·과실주가 뜻을 나눠 가져 고르기 화면에서 숨김
        hidden(
          s('fruit-wine', '과실주 (와인·사과주 포함)', 'Fruit wine (incl. grape wine, cider)'),
        ),
        sh(
          'wine',
          '와인 (레드·화이트·스파클링)',
          'Wine (red, white, sparkling)',
          '포도를 으깨 효모로 발효한다. 레드는 껍질째, 화이트는 즙만 발효한다. 품종과 침용·발효 온도, 오크 숙성 여부가 축. 국산 포도인지 수입 원액인지와 병 단위가 질문. 거품을 살린 스파클링 와인도 여기. 포도 밖의 과일은 사과주·과실주.',
        ),
        sh(
          'cider',
          '사과주·과실주',
          'Cider & fruit wine',
          '사과·배·복분자·감·블루베리처럼 포도 밖의 과일을 발효한다(시드르·페리 포함). 과일 당도와 보당, 발효 뒤 탄산·단맛 조절이 축. 국산 과일 원료와 지역 특산주 면허가 질문. 과일을 증류주에 담가 우리면 리큐르, 포도는 와인.',
        ),
      ]),
      ...inGroup('beer', [
        s('craft-beer', '맥주 (크래프트 포함)', 'Beer (incl. craft)'),
        s('non-alcoholic-beer', '논알콜 맥주', 'Non-alcoholic beer'),
      ]),
      ...inGroup('spirits', [
        // 2026-11-01: 소주 (희석식), 소주 (증류식)이 뜻을 나눠 가져 고르기 화면에서 숨김
        hidden(s('distilled-soju', '소주 (증류식 포함)', 'Soju (incl. distilled)')),
        sh(
          'soju-diluted',
          '소주 (희석식)',
          'Soju (diluted)',
          '주정(희석용 알코올)에 정제수와 감미료를 섞어 도수를 맞춘다. 도수와 감미료 배합, 물이 맛 축. 과일 맛처럼 가향하는지와 병·페트 용량이 질문. 쌀·보리를 직접 발효해 증류하면 소주 (증류식).',
        ),
        // 이름이 '증류식 소주' 가 아닌 까닭: 양식 1판 분류표의 '증류식 소주' 는 옛 세부 distilled-soju 로 받는다 (sample-import)
        sh(
          'soju-distilled',
          '소주 (증류식)',
          'Soju (distilled)',
          '쌀·보리·고구마 술덧을 단식 증류기(상압·감압)로 증류한다. 증류 방식과 원료, 숙성(옹기·스테인리스) 기간이 향 축. 도수와 숙성 연수 표기가 질문. 안동소주 같은 전통 소주 포함. 오크통에 숙성하면 숙성 증류주, 주정에 물을 타 맞추면 소주 (희석식).',
        ),
        sh(
          'baijiu',
          '고량주·백주',
          'Baijiu (kaoliang)',
          '수수(고량)·밀·쌀을 누룩으로 고체 발효해 증류하는 중국식 백주. 누룩(대곡·소곡)과 고체 발효 기간, 향형(장향·농향·청향)이 축. 도수와 숙성 연수가 질문. 국내에서 빚으면 원료 수입과 표시 기준을 확인한다.',
        ),
        sh(
          'whisky',
          '위스키',
          'Whisky',
          '맥아·곡물을 당화·발효해 증류하고 오크통에 숙성한다. 몰트·그레인·블렌디드 구분과 증류기(단식·연속식), 숙성 연수와 캐스크 종류가 축. 숙성 연수 표기와 병 단위가 질문. 숙성 원액을 사 와서 블렌딩·병입하는지가 원가를 가른다.',
        ),
        sh(
          'brandy',
          '브랜디',
          'Brandy',
          '포도주·과실주를 증류해 오크통에 숙성한다. 코냑·칼바도스 포함. 원료 과실주와 증류 횟수, 숙성 연수가 축. 사과·배 같은 국산 과일 브랜디인지가 질문.',
        ),
        // 2026-11-01: 위스키·브랜디가 따로 생겨 그 밖의 오크 숙성 증류주 자리로 hint 를 단다
        sh(
          'oak-aged',
          '숙성 증류주 (오크통 숙성)',
          'Oak-aged spirit',
          '오크통에 숙성해 색과 향을 입힌 증류주 중 위스키·브랜디에 맞지 않는 것. 오크 숙성 증류식 소주·고량주·전통 증류주 포함. 캐스크 종류(새 오크·버번·셰리)와 숙성 연수, 숙성고 온도·습도가 축. 숙성 연수 표기와 병 단위가 질문.',
        ),
        // 2026-11-01: 진, 보드카가 뜻을 나눠 가져 고르기 화면에서 숨김
        hidden(
          sh(
            'gin-vodka',
            '진·보드카 (무숙성 증류주)',
            'Gin & vodka (unaged spirits)',
            '주정이나 직접 증류한 원액에 보태니컬을 침출하거나 재증류한다. 보태니컬 배합과 증류 컷이 축. 원액을 직접 증류하는지 주정을 사 오는지가 원가를 가른다. 쌀·곡물 소주로 팔면 소주, 오크 숙성은 숙성 증류주, 당·과일 가향이 주인공이면 리큐르.',
          ),
        ),
        sh(
          'gin',
          '진',
          'Gin',
          '주정이나 직접 증류한 원액에 주니퍼베리와 보태니컬을 침출하거나 재증류한다. 보태니컬 배합(국산 재료 포함)과 증류 컷이 축. 원액을 직접 증류하는지 주정을 사 오는지가 원가를 가른다. 당·과일 향이 주인공이면 리큐르.',
        ),
        sh(
          'vodka',
          '보드카',
          'Vodka',
          '곡물·감자 원액을 연속식으로 높게 증류하고 숯으로 걸러 깨끗하게 만든다. 원료와 여과 횟수, 물이 맛 축. 향을 입힌(플레이버드) 보드카인지가 질문. 원액을 직접 증류하는지 주정을 사 오는지가 원가를 가른다.',
        ),
        sh(
          'rum',
          '럼',
          'Rum',
          '사탕수수 당밀이나 즙을 발효해 증류한다. 원료(당밀·생즙)와 증류기, 숙성 여부(화이트·골드·다크)가 축. 향신료를 넣은 스파이스드 럼인지와 숙성 연수 표기가 질문.',
        ),
        sh(
          'tequila',
          '테킬라·메스칼',
          'Tequila & mezcal',
          '아가베 심을 찌거나(테킬라) 구덩이에서 구워(메스칼) 즙을 발효해 증류한다. 아가베 품종과 굽는 방식, 숙성 등급(블랑코·레포사도·아녜호)이 축. 원산지 명칭 규정이 있어 국내에서 빚으면 이름을 쓸 수 있는지부터 확인한다.',
        ),
      ]),
      ...inGroup('liqueur-rtd', [
        s('liqueur', '리큐르', 'Liqueur'),
        s('highball', 'RTD·하이볼 (RTD 칵테일 포함)', 'Highball & RTD cocktail'),
      ]),
    ],
  },
  {
    id: 'produce',
    emoji: '🍑',
    ko: '과채가공',
    en: 'Fruit & vegetable',
    categoryId: 'fruit-vegetable',
    subs: [
      s('jam', '잼·과일 스프레드', 'Jam & fruit spread'),
      s('puree', '과채 퓨레', 'Fruit & vegetable purée'),
      s('dried', '건조 과채', 'Dried fruit & vegetables'),
      // 10/23 반박: tea.cheong(과일청)과 경계를 이쪽에도 적는다
      sh(
        'candied',
        '당절임·콤포트',
        'Candied fruit & compote',
        '과일·채소를 설탕 시럽에 졸이거나 재워 건더기째 먹는다. 정과, 콤포트, 과일 당절임. 당도와 졸이는 시간이 식감과 보존 축. 물에 타 마시는 청은 차·허브 음료 > 과일청·전통차, 빵에 바르는 잼은 잼·과일스프레드.',
      ),
      s('paste', '과채 페이스트', 'Fruit & vegetable paste'),
      s('iqf', '냉동 과채 (IQF)', 'IQF frozen produce'),
    ],
  },
  {
    id: 'grains',
    emoji: '🌾',
    ko: '곡물·두류·견과 가공',
    en: 'Grains, legumes & nuts',
    categoryId: 'grain-legume-nut',
    subs: [
      s('cereal', '시리얼·그래놀라', 'Cereal & granola'),
      s('grain-powder', '곡물가루·선식·미숫가루', 'Grain powder, seonsik & misutgaru'),
      s('soy', '두류 가공품 (콩가루·볶은콩·대두단백)', 'Soy & bean products'),
      s('nut-butter', '견과 스프레드 (땅콩버터류)', 'Nut butter'),
      s('roasted-nuts', '볶음·조미 견과', 'Roasted & seasoned nuts'),
      s('granola-bar', '곡물바·그래놀라바', 'Granola bar'),
      s('starch', '전분류', 'Starches'),
      sh(
        'muk',
        '묵 (도토리·청포·메밀)',
        'Muk (acorn & mung bean jelly)',
        '전분을 물에 풀어 쑤어 굳힌다. 전분 대 물 비율과 쑤는 시간이 탄력 축. 소비기한이 짧아 살균·충전 포장 여부가 질문. 묵가루는 전분류, 두부는 식물성·비건 대안 > 두부·템페.',
      ),
    ],
  },
  {
    id: 'oils',
    emoji: '🫗',
    ko: '유지류',
    en: 'Fats & oils',
    categoryId: 'fats-oils',
    subs: [
      s('infused', '향미유 (인퓨전 오일)', 'Infused oil'),
      s('blended', '혼합식용유 (블렌딩 오일)', 'Blended oil'),
      s('pressed', '볶음 압착유 (참기름·들기름)', 'Roasted pressed oil (sesame, perilla)'),
      s('spread', '유지 스프레드 (마가린류)', 'Fat spread (margarine)'),
      s('shortening', '쇼트닝·가공유지', 'Shortening & bakery fat'),
      s('spray', '분사형 조리유', 'Spray oil'),
      s('powdered', '분말유지', 'Powdered fat'),
    ],
  },
  {
    id: 'canned',
    emoji: '🥫',
    ko: '통조림·병조림',
    en: 'Canned & jarred',
    categoryId: 'fruit-vegetable',
    subs: [
      s('tuna', '참치캔 (유처리·양념)', 'Canned tuna', 'processed-seafood'),
      s('fruit', '과일통조림 (당액 시럽)', 'Canned fruit'),
      s('tomato', '토마토 홀·페이스트', 'Canned tomatoes (whole & paste)'),
      s('beans', '콩류통조림 (저산성)', 'Canned beans', 'grain-legume-nut'),
      s('soup', '죽·스프캔', 'Canned soup & porridge', 'meal'),
      // 2026-10-23: 기준 문서 경계 규칙 "과채 통조림은 통조림·병조림에 있다" 의 채소 자리 (과일·토마토·콩뿐이었다)
      sh(
        'vegetable',
        '채소 통조림 (옥수수·버섯·죽순)',
        'Canned vegetables',
        '옥수수·양송이·죽순 같은 저산성 채소를 염수에 담아 레토르트 살균한다. 저산성 살균 조건과 고형량 비율이 축. 토마토는 토마토 홀·페이스트, 콩은 콩류통조림, 식초 절임은 절임·저장·발효식품 > 피클.',
      ),
    ],
  },
  {
    id: 'dried',
    emoji: '🍂',
    ko: '건조·동결건조',
    en: 'Dried & freeze-dried',
    categoryId: 'fruit-vegetable',
    subs: [
      // 2026-11-01: 동결건조 과일, 동결건조 채소가 뜻을 나눠 가져 고르기 화면에서 숨김
      hidden(s('freeze-dried-produce', '동결건조 과일·채소', 'Freeze-dried fruit & vegetables')),
      // 2026-11-01 종류별 세분화 (대표 지시 "건조·동결건조 자세하게 세분화"). 동결건조 과일·채소는 명세 24번 순서대로
      // 맨 앞(숨긴 옛 칸 바로 뒤), 나머지 새 칸은 기타 건조식품 앞. 순서에 기대는 곳은 화면(타일 자리·색 번호)과 시험뿐이다
      // (옛 앱은 taxonomy-legacy 박제)
      sh(
        'freeze-dried-fruit',
        '동결건조 과일',
        'Freeze-dried fruit',
        '딸기·사과·바나나 같은 과일을 얼린 뒤 진공에서 얼음을 날려 바삭하게 말린다. 썰기 두께와 예비 동결, 건조 시간이 바삭함과 색 축. 습기를 막는 포장(질소·지퍼·방습제)과 그대로 먹는 간식인지 토핑 원료인지가 질문. 열풍으로 말린 쫀득한 과일은 건조 과일.',
      ),
      sh(
        'freeze-dried-vegetable',
        '동결건조 채소',
        'Freeze-dried vegetables',
        '채소를 데치거나 그대로 얼린 뒤 진공에서 말린다. 데치는 조건과 조각 크기, 물에 넣었을 때 살아나는 시간이 축. 즉석국·죽 원료용인지 그대로 먹는 칩인지가 질문. 라면 한 봉지용 건더기는 소스·양념·조미료 > 라면 건더기스프.',
      ),
      s('dried-greens', '건조 나물·채소', 'Dried greens & vegetables'),
      s('jerky', '육포·저키', 'Jerky', 'processed-meat'),
      sh(
        'soup-block',
        '건조 수프·블록·육수 (코인육수·다시팩)',
        'Dried soup, blocks & stock',
        '물에 풀거나 우려 쓰는 건조 수프·육수. 코인육수·고형 블록·다시팩 포함. 추출 원료 배합과 용해·우림 시간이 축. 라면 한 봉지용 스프는 소스 > 라면스프.',
        'soup-base',
      ),
      s('freeze-dried-coffee', '프리즈드라이 커피', 'Freeze-dried coffee', 'beverage'),
      sh(
        'dried-fruit',
        '건조 과일 (곶감·건포도·말린 망고)',
        'Dried fruit (persimmons, raisins, mango)',
        '과일을 썰어 열풍·냉풍·햇볕으로 말린다. 곶감·건포도·말린 망고 포함. 건조 온도와 종료 수분이 쫀득함과 보존 축, 설탕 시럽에 담갔다 말리는지와 갈변 방지가 질문. 바삭한 동결건조는 동결건조 과일, 과일·채소 칩을 함께 만드는 공정이면 과채가공 > 건조 과채.',
      ),
      sh(
        'dried-mushroom',
        '건조 버섯',
        'Dried mushrooms',
        '표고·목이·송이·포르치니를 열풍이나 햇볕에 말린다. 건조 온도와 종료 수분이 향·식감 축, 통버섯·슬라이스·가루 중 어떤 형태인지와 불렸을 때 회복률이 질문. 버섯 가루를 조미 베이스로 팔면 소스·양념·조미료 > 분말·과립 소스.',
      ),
      sh(
        'produce-powder',
        '과채 분말',
        'Fruit & vegetable powder',
        '과일·채소를 말려 곱게 갈거나 즙을 분무건조해 가루로 만든다. 단호박·비트·시금치·딸기 분말 포함. 건조 방식(열풍·동결·분무)과 입도, 색·향 유지가 축. 부형제(덱스트린) 비율과 원료 함량 표시가 질문. 물에 타 마시는 곡물 가루는 곡물·두류·견과 가공 > 곡물가루·선식·미숫가루.',
      ),
      sh(
        'herb',
        '말린 허브·향신 잎',
        'Dried herbs & aromatic leaves',
        '바질·오레가노·로즈마리·월계수잎·깻잎을 잎 모양 그대로 말린다. 건조 온도가 향 손실 축이고, 이물 선별과 살균(증기), 원산지 표시가 질문. 갈아서 섞은 가루 향신료는 소스·양념·조미료 > 향신료 블렌드·시즈닝, 우려 마시는 허브는 차·허브 음료 > 허브티·블렌드.',
      ),
      sh(
        'tofu-skin',
        '두부피·건두부',
        'Dried tofu skin & dried tofu',
        '콩물을 끓여 생긴 막을 걷어 말린 두부피(유바·푸주)와 눌러 말린 건두부. 콩물 농도와 막 걷는 온도, 건조 뒤 수분이 축. 불리는 시간과 판·말이 모양이 질문. 생두부·템페는 식물성·비건 대안 > 두부·템페, 콩가루·대두단백은 곡물·두류·견과 가공 > 두류 가공품.',
        'grain-legume-nut',
      ),
      // 2026-11-01 명세 "기타 건조식품"
      sh(
        'other',
        '기타 건조식품',
        'Other dried foods',
        '위 건조 세부(동결건조 과일·채소, 건조 과일, 건조 나물·채소, 건조 버섯, 과채 분말, 말린 허브, 두부피, 건조 수프, 프리즈드라이 커피)에 맞지 않는 건조식품. 예: 동결건조 요거트·치즈 큐브, 말린 식용 꽃, 건조 곤약. 건조 방식(열풍·동결·냉풍·햇볕)과 종료 수분·수분활성도, 습기를 막는 포장이 축. 원물째인지 분말인지가 질문. 고기 육포는 축산가공 > 육포·저키, 조미 건어물은 수산가공 > 조미 건어물.',
      ),
    ],
  },
  {
    id: 'baby',
    emoji: '🍼',
    ko: '유아·영유아식',
    en: 'Baby food',
    categoryId: 'other',
    subs: [
      s('pouch', '이유식 파우치 (퓨레)', 'Baby food pouch'),
      s('puffs', '유아 과자·퍼프', 'Baby snacks & puffs', 'snacks'),
      s('formula', '분유·조제식', 'Formula'),
      s('drink', '유아 음료', "Kids' drink", 'beverage'),
    ],
  },
  {
    // 주식은 dry-food·wet-pouch, 간식은 baked·freeze-dried·dental
    id: 'pet',
    emoji: '🐾',
    ko: '반려동물 식품 (펫푸드·간식)',
    en: 'Pet food & treats',
    categoryId: 'other',
    subs: [
      // 2026-11-01: 명세 말 "수제 트릿" 으로 넓힘 (옛 이름 '(구운 간식)'). 굽지 않은 수제 간식도 여기
      sh(
        'baked',
        '수제 트릿',
        'Handmade treats',
        '소량으로 직접 만드는 반려동물 간식. 오븐에 구운 비스킷·쿠키, 건조기로 말린 수제 육포·저키, 찐 간식 포함. 원료육 함량과 굽기·건조 온도, 종료 수분이 보존과 식감 축. 대상(개·고양이)과 낱개·봉 단위가 질문. 국내는 사료관리법 기준이다. 진공에서 얼려 말리면 동결건조 트릿, 씹어 이를 닦는 껌은 덴탈껌.',
      ),
      s('freeze-dried', '동결건조 트릿', 'Freeze-dried treats'),
      s('wet-pouch', '습식 파우치', 'Wet pouch'),
      s('dental', '덴탈껌', 'Dental chew'),
      // 2026-11-01 명세 "건식·습식 사료 (주식)" 으로 넓힘 (옛 결과의 건식 사료도 그대로 맞는 이름)
      sh(
        'dry-food',
        '건식·습식 사료 (주식)',
        'Dry & wet pet food (complete meals)',
        '하루 영양을 채우는 주식 사료. 건식은 압출 성형한 키블, 습식은 캔·트레이에 담아 레토르트 살균한다. 생애 단계별 영양 기준 충족과 압출·살균 조건, 기호성이 축. 대상(개·고양이)과 생애 단계가 질문. 국내는 사료관리법 기준이다. 간식·토퍼용 소포장은 습식 파우치.',
      ),
    ],
  },
  // 2026-10-23 새 대분류. 식품 축에 떡 자리가 없었다 (메뉴 축 디저트 > 떡·한과만). 식품공전도 떡류를 과자·빵과 같은 묶음의
  // 독립 유형으로 둔다. 명세 26대 밖이라 명세 순서 뒤, 기타 앞
  {
    id: 'tteok',
    emoji: '🍡',
    ko: '떡·한과',
    en: 'Rice cakes & Korean confections',
    categoryId: 'dessert',
    hint: '쌀·찹쌀을 찌거나 쳐서 만드는 떡과, 반죽을 지지거나 튀겨 조청으로 굳힌 한과. 축은 찌기·치기·지지기 공정과 굳는 속도. 당일 판매인지 급속냉동 유통인지가 첫 질문.',
    subs: [
      sh(
        'steamed',
        '찐 떡 (설기·시루떡·송편)',
        'Steamed rice cakes',
        '쌀가루를 시루나 스팀에 쪄서 만든다. 쌀가루 물주기와 찌는 시간이 품질 축. 굳음이 빨라 당일 판매인지 급속냉동인지가 질문. 떡케이크(설기 케이크)도 여기.',
      ),
      sh(
        'pounded',
        '친 떡 (인절미·절편·가래떡)',
        'Pounded rice cakes',
        '찐 쌀·찹쌀을 쳐서 찰기를 낸다. 펀칭기·제병기 설비와 고물(콩가루·팥)이 축. 굳음을 늦출 방법과 냉동 뒤 해동 품질이 질문. 속을 채우면 찹쌀떡·모찌.',
      ),
      sh(
        'filled',
        '찹쌀떡·모찌 (속 채운 떡)',
        'Filled rice cakes & mochi',
        '찹쌀 반죽으로 앙금·생크림·과일을 감싼다. 빚는 개수와 속 배합이 축. 생크림·과일이 들어가면 냉동 유통과 해동 뒤 소비기한이 질문. 예: 찹쌀떡, 딸기모찌, 생크림 찹쌀떡.',
      ),
      sh(
        'cooking',
        '떡볶이떡·떡국떡 (요리용 떡)',
        'Rice cakes for cooking',
        '쌀떡·밀떡을 뽑아 썰어 요리 재료로 판다. 쌀·밀 배합과 굳음 방지 처리가 상온·냉장·냉동 유통을 가른다. 양념까지 든 떡볶이는 밀키트 > 떡볶이·분식 밀키트.',
        'grain-legume-nut',
      ),
      sh(
        'hangwa',
        '한과 (약과·유과·강정)',
        'Korean confections (yakgwa, yugwa, gangjeong)',
        '반죽을 기름에 지지거나 튀겨 조청·꿀에 집청한다. 튀김 온도와 집청 당도가 식감과 유통기한 축. 산패 관리와 낱개 포장이 질문. 예: 약과, 유과, 쌀강정, 깨강정, 다식. 시리얼·견과를 시럽으로 굳힌 바는 스낵·과자 > 시리얼바·견과바.',
      ),
      sh(
        'yanggaeng',
        '양갱·앙금 (팥·밤·고구마)',
        'Yanggaeng & sweet bean paste',
        '팥·밤·고구마를 삶아 앙금을 내고 한천과 졸여 굳힌다. 앙금 수분·당도와 한천 비율이 축. 떡·빵 공장에 넘기는 앙금 원료, 빙수팥도 여기.',
      ),
    ],
  },
  {
    id: 'misc',
    emoji: '🧂',
    ko: '기타',
    en: 'Other',
    categoryId: 'other',
    subs: [
      s('powder', '분말', 'Powder mix'),
      // 2026-10-24: 소스 > 시즈닝·향신료, 스낵·과자 > 스낵·칩이 같은 뜻이라 고르기 화면에서 숨김 (53_중복세부_숨김표)
      hidden(s('seasoning', '시즈닝', 'Seasoning', 'sauce')),
      hidden(s('snack', '스낵', 'Snack', 'snacks')),
      s('treat', '간식', 'Treats', 'snacks'),
      sh(
        'dessert',
        '디저트',
        'Dessert',
        '디저트 전반. 떡·한과, 제과·제빵, 빙과, 푸딩·컵 디저트의 세부에 맞으면 그 공정 기준을 따른다.',
        'dessert',
      ),
      // 2026-10-24: 제과·제빵 대분류(빵·페이스트리, 쿠키, 케이크)가 뜻을 그대로 담아 고르기 화면에서 숨김
      hidden(
        sh(
          'bakery',
          '베이커리',
          'Bakery',
          '빵·과자 전반. 제과·제빵이나 떡·한과의 세부에 맞으면 그 공정 기준을 따른다.',
          'bakery',
        ),
      ),
      // 2026-10-24: 절임·저장·발효식품 > 김치, 장아찌, 발효절임이 같은 뜻이라 고르기 화면에서 숨김
      hidden(s('fermented', '발효식품 (김치·장아찌)', 'Fermented food', 'fermented')),
      // 10/23 반박: 버터가 유제품 > 버터·크림에도 생겨 이쪽은 섞어 바르는 스프레드로 안내한다
      sh(
        'spread',
        '스프레드 (잼/버터류)',
        'Spread (jam, butter)',
        '잼·버터·꿀·앙금을 섞어 빵에 바르는 스프레드. 꿀버터, 앙버터, 과일버터 같은 것. 버터·크림 자체는 유제품 > 버터·크림, 과일잼만이면 과채가공 > 잼, 마가린은 유지류 > 유지 스프레드, 땅콩버터는 곡물·두류·견과 가공 > 견과 스프레드.',
        'fruit-vegetable',
      ),
      s('syrup-topping', '시럽·토핑소스', 'Syrup & topping sauce', 'sauce'),
      // 2026-10-24: 소스 > 쌈장이 담고 튜브는 포장 화면에서 고르는 것이라 고르기 화면에서 숨김
      hidden(s('ssamjang-tube', '숙성 쌈장 튜브형', 'Aged ssamjang (tube)', 'sauce')),
      sh(
        'honey',
        '꿀·조청 (벌꿀·꿀스틱·쌀조청)',
        'Honey & rice syrup (jocheong)',
        '벌꿀은 채밀·숙성과 꽃 종류, 조청은 엿기름 당화와 졸임 농도가 축. 꿀스틱 소분과 결정화 관리가 질문. 사양벌꿀 표시 구분을 확인한다. 과일·생강을 꿀에 재운 청은 차·허브 음료 > 과일청·전통차, 설탕 시럽·토핑은 시럽·토핑소스.',
      ),
    ],
  },
]

// ── 메뉴(요리) 축: 8 대·96 세부 ─────────────────────────────────────
// 2026-10-23: 세부 4개 (rice-noodle.tteokbokki·dumpling, intl.egg-brunch, drinks.cocktail) 를 각 subs 끝에.
// hint = 문서의 "갈리는 축" 요약. 프롬프트가 장비·선조리·인분·원가 축을 잡는 데 쓴다.
// 2026-10-22: 국제·서양 메인(intl)을 밥·면 뒤에 더했다. 판정 순서: 탄수화물 베이스가 파스타·리조또·곡물·도우·빵이고
// 그것이 한 끼의 중심이면 intl, 아니면 기존 7대. 두 자리가 맞으면 메뉴판 이름으로 정한다.
// 스테이크는 main.roast 그대로(서양식인지는 style 꼬리표), 치즈 플래터는 anju.finger 그대로(코스면 course=cheese).
// 2026-11-01: main.sushi·side.tapas 를 각 subs 끝에, 국제·서양 메인 세부 16개를 묶음 안에 (위 파일 머리 설명).

/**
 * 국제·서양 메인 고르기 화면의 묶음 (명세 2장 순서). 브런치는 명세 밖의 옛 세부 하나라 마지막.
 * 한 줄 호출로 쓴다: 사진 매핑 생성 스크립트가 4칸 들여쓴 `id: '…',` 줄을 대분류 id 로 읽는다
 */
const INTL_GROUPS: SubGroup[] = [
  subGroup('pasta', '파스타', 'Pasta', 'パスタ', '意面'),
  subGroup('risotto-grain', '리조또·그레인', 'Risotto & grains', 'リゾット・グレイン', '烩饭·谷物'),
  subGroup('pizza', '피자·플랫브레드', 'Pizza & flatbread', 'ピザ・フラットブレッド', '披萨·薄饼'),
  subGroup(
    'burger-sandwich',
    '버거·샌드위치',
    'Burgers & sandwiches',
    'バーガー・サンドイッチ',
    '汉堡·三明治',
  ),
  subGroup('main-salad', '메인 샐러드', 'Main salads', 'メインサラダ', '主菜沙拉'),
  subGroup('brunch', '브런치', 'Brunch', 'ブランチ', '早午餐'),
]

/**
 * 새 세부가 뜻을 다 나눠 가진 국제·서양 메인의 옛 세부 (2026-11-01). 기타 중복(10/24)과 같은 hiddenInPicker 라
 * 저장된 결과·옛 앱 요청·라벨·프롬프트는 그대로다. 고르기 화면에 다시 보이게 하려면 여기서 빼기만 하면 된다
 */
const INTL_SPLIT_INTO_NEW = new Set([
  'intl.risotto-grain',
  'intl.pizza-flatbread',
  'intl.main-salad',
  // 2026-11-01 전수 확인 13번: 수제 버거와 같은 칸 둘로 보였다. 랩·타코·부리또는 따로 고를 자리가 없어진다
  'intl.burger-sandwich',
])

export const MENU_MAJORS: MajorCategory[] = [
  {
    id: 'main',
    emoji: '🍖',
    ko: '메인 요리',
    en: 'Mains',
    categoryId: 'meal',
    hint: '접시에 단독으로 올라가는 주 요리. 중분류 축은 열을 넣는 방식(건열/습열/유열/무가열)과 그에 딸린 장비·선조리 가능 여부.',
    subs: m('main', '메인 요리', 'Mains', '', [
      [
        'grill',
        '구이·직화',
        'Grilled & charbroiled',
        '건열 직화. 열원(숯·가스그릴·샐러맨더)과 누가 굽는가(홀 테이블 vs 주방)로 갈린다. 테이블 구이면 1인분 중량·부위가 핵심 질문이고 원육 시세에 종속돼 객단가를 먼저 고정할 수 없다. 예: 삼겹살 소금구이, 양념갈비, 고등어구이, 닭꼬치',
      ],
      [
        'wok',
        '볶음 (웍)',
        'Stir-fry (wok)',
        '고화력 건열+유지. 웍 1회 투입량이 조리 단위 — 한 번에 몇 인분까지 볶는가. 맛은 양념장 배합(고추장·간장·기름) 중심. 예: 제육볶음, 오징어볶음, 낙지볶음, 고추잡채',
      ],
      [
        'braise',
        '조림',
        'Braised',
        '습열 졸임. 양념 국물을 재료에 붙여 남기지 않는다. 배치로 미리 조려 재가열해 내므로 주문 후 조리시간이 아니라 선조리 보관 단위와 재가열 방식이 축. 예: 고등어조림, 코다리조림, 장조림, 두부조림',
      ],
      [
        'steam',
        '찜 (밀폐·압력)',
        'Steamed (pressure)',
        '수증기·압력 습열. 조리시간이 길고 냄비 단위가 커서 인분이 2~3인 상 단위로 고정. 객단가는 1인가가 아니라 상 단가. 예: 갈비찜, 안동찜닭, 아귀찜, 순대곱창찜',
      ],
      [
        'fry',
        '튀김·프라이',
        'Fried',
        '유열(기름). 튀김기 대수와 유온 회복력이 피크 처리량 상한. 홀딩 타임이 짧아 튀긴 뒤 몇 분 안에 나가는가·소스 별첨 여부가 추가되고 유지 교체·폐유가 원가 항목. 예: 돈까스, 탕수육, 후라이드치킨, 새우튀김',
      ],
      [
        'pancake',
        '전·부침 (철판)',
        'Pan-fried pancakes',
        '얕은 유지+철판 면적. 동시에 몇 장을 부치느냐가 처리량. 주재료보다 반죽 대비 건더기 비율이 원가와 맛을 좌우. 예: 해물파전, 김치전, 동그랑땡, 감자전',
      ],
      [
        'raw',
        '회·생식 (무가열)',
        'Raw & sashimi',
        '무가열. 칼 기술·냉장·선도 회전이 축. 난이도는 손질 숙련도와 수율, 매일 시세가 흔들려 객단가는 시가. 예: 광어회, 연어회, 육회, 회무침. 샤리에 쥐는 초밥과 일식 사시미 구성은 메인 요리 > 초밥·니기리·사시미.',
      ],
      [
        'roast',
        '스테이크·로스트 (오븐·팬)',
        'Steak & roast',
        '주방에서 완결되는 건열(팬 시어링·오븐). 심부온도(굽기 정도)가 품질 축 — 도네스 선택을 받을 것인가. 주문 후 대기가 길어 사이드·코스 구성이 따라붙는다. 예: 채끝 스테이크, 로스트치킨, 바비큐 폭립, 양갈비',
      ],
      [
        'smoke',
        '훈연·바비큐 (저온 장시간)',
        'Smoked & BBQ (low & slow)',
        '저온 장시간 훈연. 조리가 영업시간을 넘겨 전일 준비가 전제 — 하루 몇 인분을 미리 뽑고 남으면 어떻게 소진하는가. 스모커 투자와 감량(수율)이 원가 축. 예: 훈제오리, 브리스킷, 풀드포크, 스모크 치킨',
      ],
      // 2026-11-01: 회(main.raw)와 나눈 일식 스시. 서버는 세부 hint 를 프롬프트에 그대로 넣는다
      [
        'sushi',
        '초밥·니기리·사시미',
        'Sushi, nigiri & sashimi',
        '샤리(배합초 밥)에 네타를 손으로 쥐어 바로 내는 초밥과 일식 사시미. 네타 숙성·손질 수율과 샤리 온도·간, 쥐는 사람의 숙련도가 축이고 오마카세면 1인 피스 수와 코스 순서가 질문. 네타가 시가라 객단가도 시가에 묶인다. 썰어 내는 한식 회는 메인 요리 > 회·생식, 포장 초밥 도시락은 밥·면 > 성형밥·포장밥. 예: 모둠 니기리, 참치 사시미, 오마카세, 지라시',
      ],
    ]),
  },
  {
    id: 'rice-noodle',
    emoji: '🍚',
    ko: '밥·면',
    en: 'Rice & noodles',
    categoryId: 'meal',
    hint: '탄수화물 베이스가 주인공. 축은 베이스 형태(밥/면/떡·만두피)와 소스·국물이 붙는 방식(덮기·볶기·비비기·말기·차게 내기). 밥은 취반·홀딩이, 면은 삶기 설비와 불는 시간이 상한.',
    subs: m('rice-noodle', '밥·면', 'Rice & noodles', '', [
      [
        'rice-bowl',
        '덮밥·돈부리',
        'Rice bowls (donburi)',
        '밥과 토핑을 따로 조리해 얹는 형태. 소스 배합이 표준화 축. 1인 그릇 고정이라 인분 질문이 사라지고 토핑 선조리 가능 여부와 밥 홀딩 시간이 질문. 예: 제육덮밥, 회덮밥, 오야꼬동, 규동, 카레라이스',
      ],
      [
        'fried-rice',
        '볶음밥·철판밥',
        'Fried rice & teppan rice',
        '고화력 웍·철판. 밥의 수분 상태(전날 밥·급속냉각)가 품질을 좌우. 어떤 밥을 쓰는가와 1회 볶는 인분 상한이 질문, 자투리 활용이 원가 축. 예: 김치볶음밥, 새우볶음밥, 오므라이스, 철판제육볶음밥',
      ],
      [
        'bibimbap',
        '비빔밥·고명 조립형',
        'Bibimbap & topping-assembled',
        '주문 시점 가열이 거의 없는 조립. 고명 가짓수가 곧 준비 인력이자 원가. 조리시간은 주문 후가 아니라 오픈 전 선작업 시간. 예: 전주비빔밥, 산채비빔밥, 육회비빔밥, 보리비빔밥',
      ],
      [
        'pot-rice',
        '솥밥·가마솥밥 (주문 후 취반)',
        'Pot rice (cooked to order)',
        '주문 받고 밥을 짓는 형태. 개별 솥 보유 수가 동시 좌석 상한. 손님을 몇 분 기다리게 할 것인가와 솥 수량이 질문, 대기를 보상해야 하므로 객단가가 위로. 예: 전복솥밥, 굴솥밥, 소고기솥밥, 표고솥밥',
      ],
      [
        'shaped-rice',
        '성형밥·포장밥',
        'Shaped & packed rice',
        '무가열 성형. 밥 간(배합초·소금)과 손 작업 시간이 축. 포장·테이크아웃이 기본이라 만든 뒤 몇 시간까지 파는가가 질문, 단위는 인분이 아니라 개수·줄 수. 예: 김밥, 유부초밥, 삼각주먹밥, 포장 초밥 도시락. 스시 카운터에서 쥐어 바로 내는 초밥은 메인 요리 > 초밥·니기리·사시미.',
      ],
      [
        'porridge',
        '죽·포리지',
        'Porridge & congee',
        '전분을 수분에 풀어 익히는 형태. 물 비율과 계속 저어야 하는 시간이 화구를 길게 점유. 농도(되기)와 미리 쑤어 둘 수 있는지가 질문, 저작 부담이 낮아 연령·회복식 타깃. 예: 전복죽, 야채죽, 호박죽, 팥죽. 리조또는 국제·서양 메인 > 리조또·그레인.',
      ],
      [
        'soup-noodle',
        '국물면 (온면)',
        'Hot noodle soup',
        '육수와 면을 따로 만들어 마지막에 합치는 형태. 면 삶는 설비와 불는 시간이 배선 속도 상한. 육수를 직접 뽑는가가 갈림길이고 뽑는다면 국·탕 개발이 통째로 딸려온다. 예: 잔치국수, 우동, 칼국수, 라멘, 짬뽕, 쌀국수',
      ],
      [
        'dry-noodle',
        '비빔면·무국물면',
        'Dry & mixed noodles',
        '국물 없이 양념장으로 완성. 육수 설비 없이 진입, 소스 배합이 맛의 전부. 매운맛·산미 단계 설계로 좁혀지고 여름 편중이 커서 계절 질문 필수. 예: 비빔국수, 쫄면, 골뱅이소면, 막국수, 짜장면',
      ],
      [
        'pan-noodle',
        '볶음면·팬면',
        'Stir-fried noodles',
        '삶아 둔 면을 웍에 볶아 소스로 코팅. 사전 삶기 재고 관리가 축, 1회 볶는 양 제한. 면을 미리 몇 인분 삶아 둘 것인가와 소스 농도(면에 붙는 정도)가 질문. 예: 야끼소바, 볶음우동, 팟타이, 상하이볶음면',
      ],
      [
        'cold-noodle',
        '냉면·냉국수',
        'Cold noodles',
        '차게 내는 면. 육수·면수 냉각 보관 설비가 진입 조건, 계절 편중 극심. 단품이 아니라 비수기를 무엇으로 메울 것인가라는 로테이션 질문. 예: 물냉면, 평양냉면, 콩국수, 냉모밀',
      ],
      [
        'tteokbokki',
        '떡볶이·떡 요리',
        'Tteokbokki & rice cake dishes',
        '떡이 주인공인 분식. 소스(고추장·짜장·로제) 배합과 떡 불림, 큰 판에 졸여 두고 퍼 담는 홀딩 시간이 축. 분식 판 홀딩인지 즉석 테이블 가열인지, 사리·튀김 추가가 질문. 예: 국물떡볶이, 즉석떡볶이, 로제떡볶이, 궁중떡볶이',
      ],
      [
        'dumpling',
        '만두·딤섬',
        'Dumplings & dim sum',
        '면피에 소를 싸서 찌거나 굽거나 삶는다. 소 선조리와 빚는 개수가 인력 축이고 1인분은 개수 단위. 직접 빚는지 냉동 만두를 쓰는지, 찜기·철판 수가 질문. 만둣국은 국·탕, 속 채운 파스타는 국제·서양 메인 > 필링 파스타. 예: 손만두, 군만두, 샤오롱바오, 하가우',
      ],
    ]),
  },
  {
    id: 'intl',
    emoji: '🍝',
    ko: '국제·서양 메인',
    en: 'International & Western mains',
    categoryId: 'meal',
    hint: '서양·국제 메뉴 중 밥·면, 메인 요리 나무에 자리가 없던 형태(파스타, 리조또, 피자, 버거, 메인 샐러드, 브런치 에그 요리). 축은 탄수화물 베이스(면·쌀·곡물·도우·빵)와 그것을 마무리하는 방식(버무림, 마감 저어 익힘, 굽기, 조립), 선조리할 수 있는 부분과 주문 후 마감 시간.',
    // 파스타 4형태는 3단 화면 대신 세부 4개로 펼쳤다. 아시아 웍 볶음면은 rice-noodle.pan-noodle
    // 2026-11-01: 세부를 명세 2장의 묶음 순서로 놓는다 (intl 은 1.0.3·1.0.4 가 모르는 대분류라 지킬 옛 타일 자리가 없다).
    // 옛 세부 9개의 id·hint 는 그대로 두고 새 세부를 각 묶음 안에 넣었다. 버거·샌드위치·랩은 랩 자리라 묶음 끝.
    // 같은 날 전수 확인 뒤: 파스타 4개 이름을 명세 말 순서로(드라이 파스타 등), 버거·샌드위치·랩은 고르기 화면에서 숨김
    groups: INTL_GROUPS,
    subs: m('intl', '국제·서양 메인', 'International & Western mains', '', [
      [
        'pasta-dry',
        '드라이 파스타',
        'Dried pasta',
        '건면(스파게티·펜네 등)을 삶아 소스와 버무리는 형태. 면 삶기 설비와 소스 선조리가 축. 1인 건면 중량(80~120g)과 소스 배치 단위, 면수 활용이 원가. 예: 알리오올리오, 카르보나라, 아라비아타, 봉골레',
        'pasta',
      ],
      [
        'pasta-fresh',
        '프레시 파스타',
        'Fresh pasta',
        '생면을 매장에서 반죽·성형. 반죽 노동과 당일 소진이 축이고 생면 수율과 보관 시간(냉장 1~2일)이 질문. 객단가가 건면보다 높다. 예: 탈리아텔레, 파파르델레, 오레키에테, 생면 라구',
        'pasta',
      ],
      [
        'pasta-filled',
        '필링 파스타 (라비올리·토르텔리니)',
        'Filled pasta (ravioli, tortellini)',
        '속을 채운 파스타. 필링 선조리와 성형 개수가 인력 축이고 1인분은 개수 단위. 냉동 보관 여부가 생산 방식을 가른다. 예: 라비올리, 토르텔리니, 아뇰로티, 메짤루네',
        'pasta',
      ],
      [
        'pasta-baked',
        '오븐 베이크 파스타',
        'Oven-baked pasta',
        '소스·치즈와 층을 쌓아 오븐에 굽는 형태. 팬 단위 선조리 후 조각 재가열이 축이고 1팬 조각 수와 재가열 품질이 질문. 예: 라자냐, 마카로니 그라탱, 베이크드 지티, 카넬로니',
        'pasta',
      ],
      [
        'risotto-grain',
        '리조또·그레인',
        'Risotto & grains',
        '쌀·보리·퀴노아·파로를 육수로 익히거나 곡물 볼로 조립. 리조또는 반쯤 익혀 두고 주문 후 마감하는 것이 축, 그레인 볼은 곡물 배치 조리와 토핑 조립. 예: 버섯 리조또, 해물 리조또, 퀴노아 볼, 파로 볼',
        'risotto-grain',
      ],
      [
        'risotto',
        '리조또',
        'Risotto',
        '카르나롤리·아르보리오 쌀을 육수로 저어 익히는 형태. 반쯤 익혀 식혀 두고 주문 후 육수·버터·치즈로 마감(만테카투라)하는 것이 축, 마감 15분 안팎의 화구 점유와 1인 쌀 70~90g 이 질문. 보리 리조또 포함. 예: 버섯 리조또, 해물 리조또, 사프란 리조또',
        'risotto-grain',
      ],
      [
        'orzo',
        '오르조',
        'Orzo',
        '쌀알 모양 파스타. 리조또처럼 육수로 졸여 마감하거나(오르조토) 삶아 식혀 샐러드로 쓴다. 따뜻하게 낼지 차게 낼지와 불는 시간이 축, 1인 건면 70~90g. 예: 새우 오르조토, 토마토 오르조, 오르조 샐러드',
        'risotto-grain',
      ],
      [
        'farro',
        '파로',
        'Farro',
        '고대 밀(엠머) 통곡물. 불리고 삶는 데 30~40분이 걸려 배치로 삶아 두고 주문 후 데워 마감한다. 톡톡 씹히는 식감 유지와 삶은 곡물 보관 기간(냉장 3~4일)이 축. 예: 버섯 파로토, 파로 수프, 구운 채소 파로',
        'risotto-grain',
      ],
      [
        'quinoa-bowl',
        '퀴노아 그레인 볼',
        'Quinoa grain bowl',
        '퀴노아 같은 익힌 곡물을 바닥에 깔고 단백질·채소·소스를 얹어 조립하는 볼. 곡물 배치 조리와 토핑 선조리, 주문 후 조립 시간이 축이고 포장·배달 비중이 크다. 잎채소에 곡물을 섞어 버무리면 그레인 샐러드. 예: 연어 퀴노아 볼, 지중해 그레인 볼, 부다 볼',
        'risotto-grain',
      ],
      [
        'pizza-flatbread',
        '피자·플랫브레드',
        'Pizza & flatbread',
        '도우 발효와 오븐 종류(화덕·데크·컨벡션)가 축. 도우 볼을 전날 몇 개 만들고 시간당 몇 판 굽는가, 토핑 원가가 판당 원가. 예: 마르게리타, 나폴리 피자, 로마식 사각 피자, 플랫브레드',
        'pizza',
      ],
      [
        'pizza-napoli',
        '나폴리 스타일 피자',
        'Neapolitan-style pizza',
        '고수분 도우를 오래 발효해 400도 넘는 화덕(장작·가스)에서 60~90초에 굽는다. 화덕 투자와 도우 볼 발효 관리가 축, 1판 1인이고 시간당 굽는 판 수가 처리량. 예: 마르게리타, 마리나라, 디아볼라',
        'pizza',
      ],
      [
        'pizza-roman',
        '로만 스타일 피자',
        'Roman-style pizza',
        '얇고 바삭한 둥근 도우(톤다)나 가볍게 부푼 핀사를 데크 오븐에서 비교적 낮은 온도로 길게 굽는다. 밀대 성형과 바삭함이 유지되는 시간, 포장 뒤 눅눅해짐이 축. 예: 톤다 로마나, 핀사 로마나, 카초 에 페페 피자',
        'pizza',
      ],
      [
        'pizza-al-taglio',
        '피자 알 타글리오',
        'Pizza al taglio',
        '큰 사각 팬에 고수분 도우를 펴 구운 뒤 잘라 무게·조각 단위로 판다. 팬 단위 선제조와 진열 후 재가열이 축이고 조각 중량(g)과 진열 회전이 질문. 예: 감자 로즈마리 피자, 모르타델라 피자, 마르게리타 조각',
        'pizza',
      ],
      [
        'focaccia',
        '포카치아',
        'Focaccia',
        '올리브유를 두른 팬에서 고수분 도우를 발효해 두툼하게 구운 빵을 한 끼로 낸다. 팬 단위 굽기와 토핑 구성이 축, 조각 크기와 당일 소진이 질문. 곁들임 빵이면 사이드·전채 > 빵·스타터 브레드. 예: 로즈마리 포카치아, 방울토마토 포카치아, 제노바 포카치아',
        'pizza',
      ],
      [
        'flatbread',
        '플랫브레드',
        'Flatbread',
        '발효가 짧거나 없는 납작한 반죽을 철판·오븐·탄두르에 빠르게 굽고 토핑을 얹거나 싸서 낸다. 반죽 선제조와 굽는 장비가 축, 주문 후 굽기 2~4분. 예: 라흐마준, 피데, 플랫브레드 피자, 만나쉬',
        'pizza',
      ],
      [
        'burger',
        '수제 버거',
        'Handmade burger',
        '패티를 직접 갈아 성형해 그릴·철판에 굽고 번에 조립한다. 패티 중량·지방 비율과 굽기 정도, 번 토스팅이 축이고 주문 후 조립 시간과 포장 뒤 눅눅해지는 시간이 질문. 예: 치즈버거, 스매시 버거, 베이컨 버거',
        'burger-sandwich',
      ],
      [
        'club-sandwich',
        '클럽 샌드위치',
        'Club sandwich',
        '구운 식빵 세 장 사이에 닭·베이컨·채소를 층층이 쌓아 잘라 낸다. 속재료 선조리와 조립 단위가 축, 꽂이 고정과 자른 단면이 품질. 예: 클럽 샌드위치, BLT, 터키 클럽',
        'burger-sandwich',
      ],
      [
        'open-sandwich',
        '오픈 샌드위치',
        'Open sandwich',
        '빵 한 장 위에 토핑을 올려 칼·포크로 먹는다. 빵(사워도우·호밀) 토스팅과 토핑 플레이팅이 축, 토핑 선조리 비율과 브런치 피크 처리량이 질문. 예: 아보카도 토스트, 스뫼레브뢰드, 브루스케타 플레이트',
        'burger-sandwich',
      ],
      [
        'baguette-sandwich',
        '바게트 샌드위치',
        'Baguette sandwich',
        '바게트를 갈라 햄·치즈·채소를 채운다. 바게트 당일 수급과 껍질 식감 유지, 미리 만들어 둘 수 있는 시간이 축. 예: 잠봉뵈르, 반미, 로스트비프 바게트',
        'burger-sandwich',
      ],
      [
        'burger-sandwich',
        '버거·샌드위치·랩',
        'Burgers, sandwiches & wraps',
        '빵·토르티야와 속재료 조립. 패티·속재료 선조리와 주문 후 조립 시간이 축이고 포장 후 눅눅해지는 시간이 질문. 예: 수제 버거, 클럽 샌드위치, 반미, 파니니, 부리또, 타코, 케밥 랩',
        'burger-sandwich',
      ],
      [
        'main-salad',
        '메인 샐러드',
        'Main salads',
        '단백질을 올려 한 끼가 되는 샐러드. 채소 세척·보관 회전과 단백질 토핑 선조리가 축, 드레싱 분리와 포장 신선도가 질문. 곁들임 샐러드(사이드·전채)와 달리 1인 한 끼 양. 예: 그릴 치킨 샐러드, 연어 포케 볼, 스테이크 샐러드, 니수아즈',
        'main-salad',
      ],
      [
        'salad-protein',
        '프로틴 샐러드',
        'Protein salad',
        '구운 닭·스테이크·연어·두부 같은 단백질을 올려 한 끼가 되는 샐러드. 단백질 선조리와 채소 세척·보관 회전이 축, 1인 단백질 중량(100~150g)과 드레싱 분리가 질문. 예: 그릴 치킨 샐러드, 스테이크 샐러드, 니수아즈, 연어 포케',
        'main-salad',
      ],
      [
        'salad-grain',
        '그레인 샐러드',
        'Grain salad',
        '잎채소에 익힌 곡물(퀴노아·파로·보리·쿠스쿠스)을 섞어 드레싱으로 버무린다. 곡물 배치 조리와 드레싱 흡수(시간이 지나면 눅눅해짐)가 축, 포장 판매면 드레싱 분리가 질문. 곡물을 바닥에 깔고 얹어 조립하면 퀴노아 그레인 볼. 예: 타불레, 퀴노아 케일 샐러드, 보리 샐러드',
        'main-salad',
      ],
      [
        'salad-warm',
        '웜 샐러드',
        'Warm salad',
        '구운 채소·버섯·곡물·단백질을 따뜻한 채로 올리고 따뜻한 드레싱이나 비네그레트로 마감한다. 주문 후 굽기·볶기가 들어가 화구를 쓰는 것이 축, 잎채소가 숨 죽기 전에 내는 시간이 질문. 예: 웜 버섯 샐러드, 구운 뿌리채소 샐러드, 리옹식 샐러드',
        'main-salad',
      ],
      [
        'egg-brunch',
        '브런치 에그 요리',
        'Brunch egg dishes',
        '계란이 주인공인 브런치 접시. 수란·스크램블·오믈렛을 주문 뒤 마감해 홀딩이 거의 안 되는 것이 축. 빵·감자·샐러드를 곁들인 한 접시 구성과 피크 시간 화구 점유가 질문. 프렌치토스트·팬케이크는 디저트 > 플레이팅 디저트, 오므라이스는 밥·면 > 볶음밥·철판밥. 예: 에그 베네딕트, 오믈렛, 샥슈카, 에그 로열',
        'brunch',
      ],
    ]).map((sub) => (INTL_SPLIT_INTO_NEW.has(sub.id) ? hidden(sub) : sub)),
  },
  {
    id: 'soup',
    emoji: '🍲',
    ko: '국·탕·찌개',
    en: 'Soups & stews',
    categoryId: 'meal',
    hint: '국물이 주인공. 축은 ①국물 농도(맑음↔유백색↔얼큰 걸쭉) ②육수를 얻는 방식(짧게 우림/장시간 고아냄/주문 후 즉석) ③제공 단위(1인 그릇 vs 테이블 다인 가열).',
    subs: m('soup', '국·탕·찌개', 'Soups & stews', '', [
      [
        'clear',
        '맑은국 (즉석형)',
        'Clear soup',
        '짧게 끓이는 맑은 국물. 뼈를 고지 않아 설비·시간 부담 최저. 단품보다 정식 구성품이라 객단가가 아니라 무료 제공인가 세트 포함인가가 질문. 예: 미역국, 콩나물국, 북어국, 계란국',
      ],
      [
        'bone-broth',
        '곰탕·설렁탕 (장시간 추출)',
        'Bone broth soup',
        '뼈·고기를 오래 고아 유백색 국물. 대형 솥·연료·공간이 진입장벽. 레시피가 아니라 솥 용량과 하루 몇 그릇 분량을 뽑는가, 전일 준비 전제. 예: 설렁탕, 곰탕, 도가니탕, 사골국',
      ],
      [
        'gukbap',
        '국밥·탕반 (밥 동시 배선)',
        'Gukbap (soup with rice)',
        '완성된 육수에 밥을 말거나 따로 붙여 즉시 내는 형태. 조리가 아니라 배선 속도가 축. 토렴인가 따로국밥인가와 좌석 회전 목표가 질문, 1인 그릇 표준화 전제. 예: 돼지국밥, 순대국밥, 소머리국밥, 콩나물국밥',
      ],
      [
        'spicy-seafood',
        '매운탕·해물탕 (즉석 끓임·시가)',
        'Spicy fish & seafood stew',
        '손질한 생선·해물을 주문 후 끓이는 형태. 재료가 시가라 원가가 매일 흔들린다. 시가 표기·소중대 사이즈, 상 위 가열이라 2인 이상에서 시작. 예: 대구매운탕, 알탕, 해물탕, 아귀탕',
      ],
      [
        'whole',
        '보양탕·통재료 탕',
        'Whole-ingredient tonic soup',
        '통 재료 한 마리가 1인분을 고정 — 인분 질문이 몇 호(중량 규격)를 쓰는가로 바뀐다. 복날 등 성수기 편중이 커서 계절 질문 필수. 예: 삼계탕, 오리백숙, 전복삼계탕, 추어탕',
      ],
      [
        'jjigae',
        '찌개 (뚝배기 1~2인)',
        'Jjigae (stew)',
        '자작한 국물을 뚝배기에 1~2인 단위로 끓이는 형태. 동시에 올릴 수 있는 화구 수가 처리량 상한. 밥·반찬 세트 전제라 정식 구성과 리필이 질문. 예: 김치찌개, 된장찌개, 순두부찌개, 청국장',
      ],
      [
        'hearty',
        '얼큰·걸쭉 해장 계열',
        'Hearty spicy (haejang)',
        '고춧가루·들깨·다대기로 농도와 매운맛을 올린 국물. 다대기 배합이 표준, 대량 배치 가능. 매운맛 단계와 1인 뚝배기인가 2인 이상 냄비인가를 갈라 물어야 한다. 예: 육개장, 감자탕, 뼈해장국, 선지해장국, 마라탕',
      ],
      [
        'jeongol',
        '전골 (테이블 가열·다인)',
        'Jeongol (table hot pot)',
        '테이블 위 버너로 끓이며 나눠 먹는 형태. 인분 2인 이상 고정. 사리·육수 추가가 매출 축이라 추가 사리 품목·가격이 필수, 객단가는 상 단가. 예: 부대찌개, 곱창전골, 버섯전골, 낙지전골',
      ],
      [
        'shabu',
        '샤브샤브·나베 (손님 조리형)',
        'Shabu-shabu & nabe',
        '맑은 육수에 손님이 직접 익혀 먹는 형태. 주방은 육수와 슬라이스 규격만 책임. 고기 두께·채소 리필 정책·마무리 죽/면이 질문, 무한리필 여부가 원가 구조를 바꾼다. 예: 소고기 샤브샤브, 밀푀유나베, 스키야키, 월남쌈 샤브, 훠궈',
      ],
    ]),
  },
  {
    id: 'side',
    emoji: '🥗',
    ko: '사이드·전채',
    en: 'Sides & starters',
    categoryId: 'meal',
    hint: '단독으로 팔리지 않고 메인에 붙는 보조 역할. 축은 조리법 + 제공 온도 + 선조리 가능 여부 — 미리 만드는 것(절임·나물)과 주문 즉시 불을 쓰는 것(튀김·구이)이 주방 부하를 정반대로 만든다.',
    subs: m('side', '사이드·전채', 'Sides & starters', '', [
      [
        'banchan',
        '밑반찬·절임',
        'Banchan & pickles',
        '절임·발효. 주문 시 조리 동작 0, 전량 선조리·숙성 보관. 예: 배추김치, 깍두기, 오이피클, 단무지',
      ],
      [
        'namul',
        '나물·무침·냉채',
        'Seasoned greens & cold salads',
        '데치거나 생것을 양념에 버무림 + 찬 상태로 제공. 예: 콩나물무침, 시금치나물, 오이무침, 해파리냉채',
      ],
      [
        'pancake',
        '전·부침',
        'Pan-fried pancakes',
        '반죽·밀가루옷을 팬에 지짐 + 납작한 판을 잘라 나눔. 예: 김치전, 해물파전, 동그랑땡, 부추전',
      ],
      [
        'fried',
        '튀김·프라이드',
        'Fried sides',
        '기름 침지 고온 단시간. 주문 후 조리 확정, 튀김기 점유가 병목. 예: 감자튀김, 어니언링, 치즈스틱, 새우튀김',
      ],
      [
        'grilled',
        '구이·그릴 사이드',
        'Grilled sides',
        '직화·팬 구이로 수분을 날림. 화구 1구를 메인과 경합. 예: 구운 채소, 소시지 구이, 그릴 아스파라거스, 관자구이',
      ],
      [
        'salad',
        '샐러드·생채소',
        'Salads',
        '무가열 + 드레싱을 분리 또는 마지막에 결합. 예: 시저샐러드, 그린샐러드, 콜슬로, 리코타 샐러드. 곁들임 양. 단백질을 올린 한 끼 샐러드는 국제·서양 메인 > 메인 샐러드.',
      ],
      [
        'soup',
        '수프·포타주',
        'Soups & potage',
        '액체 전채 + 더운 것/찬 것이 같은 형태 안에서 갈림. 대량 선조리 후 재가열. 예: 양송이수프, 콘수프, 미네스트로네, 가스파초',
      ],
      [
        'bread',
        '빵·스타터 브레드',
        'Bread & starters',
        '메인에 동반되는 전분 운반체. 굽기는 마감 가열뿐. 예: 갈릭브레드, 포카치아, 난, 모닝빵. 빵이 곁들임일 때만. 피자·플랫브레드가 주인공이면 국제·서양 메인 > 피자·플랫브레드.',
      ],
      [
        'dip',
        '딥·스프레드 동반',
        'Dips & spreads',
        '찍어 먹는 소스 + 운반체의 2부 구성. 소스는 선조리, 운반체만 즉시 준비. 예: 후무스와 피타, 나초와 살사, 어니언딥, 명란마요와 바게트',
      ],
      // 2026-11-01: 나눠 먹는 작은 접시 구성 (명세 2장). 술안주 한두 접시면 anju.finger
      [
        'tapas',
        '타파스·스몰 플레이트',
        'Tapas & small plates',
        '여러 접시를 나눠 먹는 작은 접시(1접시 60~150g). 접시마다 선조리해 두고 주문 후 데우거나 마감해 빠르게 낸다. 1접시 단가와 한 상 접시 수, 같은 재료를 여러 접시에 돌려 쓰는 원가 설계가 축. 술과 곁들이는 한두 접시면 안주 > 바 안주·핑거푸드. 예: 감바스 알 아히요, 파타타스 브라바스, 크로케타, 핀초스',
      ],
    ]),
  },
  {
    id: 'anju',
    emoji: '🍻',
    ko: '안주',
    en: 'Bar food (anju)',
    categoryId: 'meal',
    hint: '주점 메뉴 논리 — 술을 계속 나가게 하는 게 목적. 조리법 축 위에 어떤 술과 짝인가(소주·맥주·막걸리·위스키/와인)와 몇 번째 주문인가(즉시 1차 안주 / 체류를 늘리는 2차 안주)가 겹친다.',
    subs: m('anju', '안주', 'Bar food', '', [
      [
        'dry',
        '마른안주·기본안주',
        'Dry snacks & basics',
        '조리 동작 사실상 0(건조품을 굽거나 그대로). 출수 최단, 착석 직후 1차 안주. 맥주·소주 공통. 예: 먹태, 노가리, 오징어채, 믹스넛',
      ],
      [
        'fried',
        '튀김안주',
        'Fried bar food',
        '기름 침지. 맥주·하이볼 페어링 중심축, 튀김기 용량이 피크 처리량. 예: 후라이드 치킨, 오징어튀김, 감자튀김, 모둠튀김',
      ],
      [
        'skewer',
        '구이·꼬치',
        'Grilled & skewers',
        '직화·숯불·철판 구이 + 꼬치·1인 집기 단위. 소주 페어링, 굽는 동안 술이 나감. 예: 닭꼬치, 곱창구이, 꼼장어구이, 삼겹살',
      ],
      [
        'hot-pot',
        '탕·전골 (안주형 국물)',
        'Stews & hot pots',
        '끓임 + 테이블 화구에서 계속 가열. 체류시간을 길게 만드는 장치, 소주 리필 유도. 예: 곱창전골, 감자탕, 알탕, 어묵탕',
      ],
      [
        'stir-fry',
        '볶음안주',
        'Spicy stir-fries',
        '고온 웍·철판 볶음. 매운맛 강도가 술 회전을 만드는 유일한 계열. 예: 낙지볶음, 닭발, 오돌뼈, 마라샹궈',
      ],
      [
        'pancake',
        '전·부침안주',
        'Pancakes (with makgeolli)',
        '지짐. 막걸리·동동주와 고정 짝, 비 오는 날 수요가 계열 전체를 움직임. 예: 해물파전, 김치전, 모둠전, 계란말이',
      ],
      [
        'raw',
        '회·숙회·냉안주',
        'Raw & cold plates',
        '비가열 또는 데치기만 + 냉. 원물 시세가 원가를 지배, 소주·사케 페어링. 예: 광어회, 문어숙회, 육회, 해산물 모둠',
      ],
      [
        'steamed',
        '찜·수육',
        'Steamed & boiled meats',
        '증기 찜·삶기 장시간. 객단가 최상단의 2차 메인 안주, 선삶기 여부로 출수가 갈림. 예: 아귀찜, 해물찜, 보쌈, 족발, 순대',
      ],
      [
        'finger',
        '바 안주·핑거푸드',
        'Bar bites & finger food',
        '1인 소분·포크로 집는 구성, 더운 것과 찬 것 혼재. 와인·위스키·하이볼 업태 전용. 예: 감바스, 치즈 플래터, 하몽, 소시지 플래터. 여러 접시를 나눠 한 끼로 먹는 구성이면 사이드·전채 > 타파스·스몰 플레이트.',
      ],
      [
        'finish',
        '마무리 면·밥 (시메)',
        'Finishing rice & noodles',
        '자리를 닫는 전분 마감. 조리는 단순하지만 주문 순서상 마지막에만 나가는 위치 축. 예: 볶음밥, 라면사리, 우동사리, 잔치국수',
      ],
    ]),
  },
  {
    id: 'dessert',
    emoji: '🍰',
    ko: '디저트',
    en: 'Desserts',
    categoryId: 'dessert',
    hint: '카페 주력. 대부분 주문 후 조리가 아니라 미리 만들어 진열이라 원가는 레시피보다 보관 온도대와 당일 폐기가 결정. 축은 조리법 + 보관 온도(상온·냉장·냉동) + 제조 시점(선제조 진열 vs 주문 후 마감).',
    subs: m('dessert', '디저트', 'Desserts', '', [
      [
        'baked',
        '구움과자·베이크드',
        'Baked goods',
        '오븐 굽기 + 상온 진열. 유통 여유가 가장 길어 폐기 압력이 가장 낮은 칸. 예: 스콘, 마들렌, 휘낭시에, 브라우니, 마카롱',
      ],
      [
        'cake',
        '케이크·조각케이크',
        'Cakes & slices',
        '시트+크림 층 조립 + 냉장 쇼케이스. 홀 단위로 만들어 조각으로 파는 구조라 남는 조각이 곧 손실. 예: 당근케이크, 티라미수, 치즈케이크, 생크림 케이크',
      ],
      [
        'tart',
        '타르트·파이',
        'Tarts & pies',
        '반죽 셸 + 충전물의 2단 구조. 셸은 선제조, 필링은 당일이라 공정이 둘로 쪼개짐. 예: 에그타르트, 레몬타르트, 애플파이, 바스크 치즈타르트',
      ],
      [
        'pastry',
        '빵·페이스트리',
        'Bread & pastry',
        '발효 후 굽기. 발효 리드타임이 길어 직접 반죽 대 냉동생지 선택이 운영을 통째로 가름. 예: 크루아상, 소금빵, 뺑오쇼콜라, 베이글',
      ],
      [
        'frozen',
        '냉동·빙과',
        'Frozen & shaved ice',
        '영하 보관·제공. 전용 장비 종속, 계절 편차가 가장 큼. 예: 젤라또, 소프트아이스크림, 빙수, 아포가토',
      ],
      [
        'cup',
        '컵 디저트 (푸딩·무스·젤리)',
        'Cup desserts',
        '저온 가열 후 응고 + 컵 단위 개별 충전. 처음부터 1인분으로 쪼개져 손실이 컵 단위로만. 예: 판나코타, 크렘브륄레, 밀크푸딩, 코코넛 젤리',
      ],
      [
        'tteok',
        '떡·한과 (전통)',
        'Tteok & hangwa',
        '찌기·지지기 + 쌀·곡물. 굳음(노화)이 빨라 당일 소진 전제. 예: 절편, 인절미, 약과, 개성주악',
      ],
      [
        'fried',
        '튀김 디저트',
        'Fried desserts',
        '기름 침지. 드물게 주문 후 즉시조리가 가능해 진열 폐기를 피할 수 있는 칸. 예: 츄러스, 도넛, 꽈배기, 호떡',
      ],
      [
        'plated',
        '플레이팅 디저트 (주문 후 마감)',
        'Plated desserts',
        '주문 후 조리·접시 조립 + 포크·나이프 필요. 회전율은 낮지만 체류시간과 객단가는 카페 메뉴 중 최상단. 예: 수플레 팬케이크, 크레페, 프렌치토스트, 와플',
      ],
    ]),
  },
  {
    id: 'drinks',
    emoji: '🍵',
    ko: '음료·차',
    en: 'Drinks & tea',
    categoryId: 'beverage',
    hint: '카페·바 매출 기둥. 조리법이 불이 아니라 추출·용해·희석·분쇄. 주문 후 제조가 초~분 단위라 회전율 최고, 베이스(에스프레소·찻잎·과일·유제품·사입)가 원가율을 결정, HOT/ICE가 같은 메뉴를 둘로 쪼갠다.',
    subs: m('drinks', '음료·차', 'Drinks & tea', '', [
      [
        'espresso',
        '에스프레소 베이스 커피',
        'Espresso drinks',
        '고압 추출 후 물·우유로 희석. 제조 동작이 가장 짧아 피크 회전을 떠받치는 칸. 예: 아메리카노, 카페라떼, 카푸치노, 플랫화이트',
      ],
      [
        'filter',
        '필터·콜드브루 커피',
        'Filter & cold brew',
        '저압·중력 또는 장시간 침출. 추출 시간이 길어 선추출·냉장 보관 전제. 예: 핸드드립, 콜드브루, 더치, 프렌치프레스',
      ],
      [
        'variation',
        '베리에이션 커피',
        'Specialty coffee drinks',
        '커피 + 크림·시럽·토핑의 층 조립. 부재료가 원가를 올리는 대신 객단가와 시그니처 역할. 예: 아인슈페너, 카페모카, 카라멜마키아토, 흑당라떼',
      ],
      [
        'non-coffee-latte',
        '논커피 라떼',
        'Non-coffee lattes',
        '분말·페이스트를 용해해 우유와 혼합(추출 아님). 커피를 못 마시는 동반 손님을 잡는 자리. 예: 초코라떼, 녹차라떼, 고구마라떼, 밀크티, 버블티',
      ],
      [
        'tea',
        '차·티 (침출)',
        'Tea (steeped)',
        '뜨거운 물에 잎·꽃을 침출. 준비 동작과 설비 요구가 가장 적음. 예: 얼그레이, 캐모마일, 페퍼민트, 보이차',
      ],
      [
        'traditional-tea',
        '전통차·한방차',
        'Traditional & herbal tea',
        '장시간 달임 또는 당에 재운 청을 희석. 선제조 필수, 수요가 겨울에 쏠림. 예: 유자차, 생강차, 대추차, 쌍화차',
      ],
      [
        'juice-ade',
        '주스·에이드',
        'Juices & ades',
        '착즙 또는 청 희석 + 탄산 유무. 과일·청 시세가 원가를 직접 흔드는 유일한 칸. 예: 오렌지주스, 자몽에이드, 레몬에이드, 청포도에이드',
      ],
      [
        'smoothie',
        '스무디·프라페',
        'Smoothies & frappés',
        '얼음과 함께 블렌더 분쇄. 제조 시간과 소음이 길어 피크타임 병목. 예: 딸기스무디, 망고스무디, 요거트스무디, 커피 프라페',
      ],
      [
        'shake',
        '셰이크·유제품 음료',
        'Shakes & dairy drinks',
        '유제품·아이스크림이 주체 + 농도 진한 마시는 디저트. 디저트 대체재로 작동. 예: 초코셰이크, 바닐라셰이크, 딸기우유, 미숫가루',
      ],
      [
        'bottled',
        '탄산·병음료 (사입 완제품)',
        'Bottled & canned (bought-in)',
        '조리 동작 0(사입 상품 그대로 제공). 제조가 아니라 재고 회전만 관리하는 마진 구조가 다른 칸. 예: 콜라, 사이다, 탄산수, 병 생수',
      ],
      [
        'cocktail',
        '칵테일·하이볼 (매장 제조)',
        'Cocktails & highballs (in-house)',
        '술을 계량해 섞고 얼음·가니시로 마감한다. 잔당 술 용량이 원가 축, 시럽·청 선제조와 제조 동작 수가 피크 병목. 무알콜 목테일 포함. 병·캔을 그대로 내면 탄산·병음료. 예: 하이볼, 진토닉, 모히토, 에스프레소 마티니',
      ],
    ]),
  },
]

/** 코스·테이스팅 축 (course-taxonomy.ts). 스타일·기법 이름을 메뉴 분류에서 읽으므로 메뉴 대분류 다음에 만든다 */
const COURSE = buildCourseTaxonomy(MENU_MAJORS)

/** 코스 축의 일본어·중국어 이름 (키 형식은 TAXONOMY_I18N 과 같다: "course:역할", "course:역할:역할.항목") */
export const COURSE_TAXONOMY_I18N = COURSE.i18n

export const TAXONOMY: Record<Axis, MajorCategory[]> = {
  product: PRODUCT_MAJORS,
  menu: MENU_MAJORS,
  course: COURSE.majors,
}

export function findMajor(axis: Axis, majorId: string): MajorCategory | undefined {
  return TAXONOMY[axis].find((x) => x.id === majorId)
}

export function findSub(axis: Axis, majorId: string, subId: string): SubCategory | undefined {
  return findMajor(axis, majorId)?.subs.find((x) => x.id === subId)
}

/** 새 앱 고르기 화면에 보이는 세부. hiddenInPicker 를 뺀 나머지, 순서는 subs 그대로 */
export function pickerSubs(major: MajorCategory): SubCategory[] {
  return major.subs.filter((x) => !x.hiddenInPicker)
}

/** 고르기 화면의 한 묶음. group 이 null 이면 작은 제목 없이 그린다 */
export interface PickerSection {
  group: SubGroup | null
  subs: SubCategory[]
}

/**
 * 고르기 화면 묶음 (2026-11-01). groups 가 없는 대분류는 제목 없는 묶음 하나(지금과 같다).
 * groups 가 있으면 그 순서로, 묶음 안은 subs 순서로. 묶음이 없는 세부는 끝에 제목 없이. 빈 묶음은 뺀다
 */
export function pickerSections(major: MajorCategory): PickerSection[] {
  const shown = pickerSubs(major)
  const groups = major.groups ?? []
  if (groups.length === 0) return [{ group: null, subs: shown }]
  const known = new Set(groups.map((g) => g.id))
  return [
    ...groups.map((group) => ({ group, subs: shown.filter((s) => s.group === group.id) })),
    { group: null, subs: shown.filter((s) => !s.group || !known.has(s.group)) },
  ].filter((section) => section.subs.length > 0)
}

/** 묶음 작은 제목. ko·en·ja·zh 밖의 언어는 영어 */
export function subGroupLabel(group: SubGroup, language: string): string {
  if (language === 'ko' || language === 'ja' || language === 'zh') return group[language]
  return group.en
}

/** 대·중카테고리 → 파이프라인 categoryId (18개). 못 찾으면 null */
export function resolveCategoryId(
  axis: Axis,
  majorId: string,
  subId?: string | null,
): ProductCategoryId | null {
  const major = findMajor(axis, majorId)
  if (!major) return null
  const sub = subId ? major.subs.find((x) => x.id === subId) : undefined
  return sub?.categoryId ?? major.categoryId
}

/** 일본어·중국어 이름표. 식품·메뉴는 taxonomy-i18n, 코스는 COURSE_TAXONOMY_I18N (키 형식은 같다) */
function i18nOf(key: string): { ja: string; zh: string } | undefined {
  return TAXONOMY_I18N[key] ?? COURSE_TAXONOMY_I18N[key]
}

/** 대분류 라벨 (ko·en 은 내장, ja·zh 는 i18nOf, 없으면 영어) */
export function majorLabel(axis: Axis, major: MajorCategory, language: string): string {
  if (language === 'ko') return major.ko
  if (language === 'ja' || language === 'zh') {
    return i18nOf(`${axis}:${major.id}`)?.[language] ?? major.en
  }
  return major.en
}

/** 중분류 라벨 */
export function subLabel(axis: Axis, majorId: string, sub: SubCategory, language: string): string {
  if (language === 'ko') return sub.ko
  if (language === 'ja' || language === 'zh') {
    return i18nOf(`${axis}:${majorId}:${sub.id}`)?.[language] ?? sub.en
  }
  return sub.en
}

/** 화면·프롬프트용 라벨 */
export function taxonomyLabel(
  axis: Axis,
  majorId: string,
  subId: string | null | undefined,
  language: string,
): { major: string; sub: string | null } {
  const major = findMajor(axis, majorId)
  const sub = subId ? major?.subs.find((x) => x.id === subId) : undefined
  return {
    major: major ? majorLabel(axis, major, language) : majorId,
    sub: sub ? subLabel(axis, majorId, sub, language) : (subId ?? null),
  }
}

/** 화면 한 줄 '대분류 · 중분류' (중분류가 없으면 대분류만). 글 카드·완성 사례 카드·결과 꼬리표 줄이 같이 쓴다 */
export function taxonomyLine(
  axis: Axis,
  majorId: string,
  subId: string | null | undefined,
  language: string,
): string {
  const { major, sub } = taxonomyLabel(axis, majorId, subId, language)
  return [major, sub].filter(Boolean).join(' · ')
}
